import { createContext, useState, useEffect, ReactNode } from "react";
import type { InstrumentTypeLabelKey } from "@/shared/lib/instrument-type";
import { LANGUAGE_STORAGE_KEY, storedLanguage } from "@/shared/lib/accept-language";

export type Language = "pt" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

const translations: Record<string, Record<Language, string>> &
  Record<InstrumentTypeLabelKey, Record<Language, string>> = {
  // Shared errors
  "errors.unreadableResponse": {
    pt: "Não foi possível ler a resposta do servidor. Tente novamente.",
    en: "Could not read the server's response. Please try again.",
  },

  // Shared - unique id (Account, Portfolio, ...)
  "uniqueId.placeholder": {
    pt: "fundo-growth",
    en: "growth-fund",
  },
  "uniqueId.hint": {
    pt: "Apenas letras minúsculas, números e hifens, de 3 a 36 caracteres.",
    en: "Lowercase letters, numbers and hyphens only, 3 to 36 characters.",
  },
  "uniqueId.length": {
    pt: "O identificador único deve ter entre 3 e 36 caracteres.",
    en: "The unique identifier must be between 3 and 36 characters.",
  },
  "uniqueId.format": {
    pt: "Use letras minúsculas, números e hifens simples, como fundo-growth.",
    en: "Use lowercase letters, numbers and single hyphens, like growth-fund.",
  },

  // Login Page
  "login.welcome": {
    pt: "Bem-vindo de volta",
    en: "Welcome back",
  },
  "login.subtitle": {
    pt: "Faça login para acessar sua conta",
    en: "Sign in to access your account",
  },
  "login.email": {
    pt: "E-mail",
    en: "Email",
  },
  "login.password": {
    pt: "Senha",
    en: "Password",
  },
  "login.emailPlaceholder": {
    pt: "seu@email.com",
    en: "your@email.com",
  },
  "login.authenticating": {
    pt: "Autenticando...",
    en: "Authenticating...",
  },
  "login.submit": {
    pt: "Entrar",
    en: "Sign In",
  },
  "login.forgotPassword": {
    pt: "Esqueceu sua senha?",
    en: "Forgot your password?",
  },
  "login.demoCredentials": {
    pt: "Credenciais de demonstração:",
    en: "Demo credentials:",
  },
  "login.successTitle": {
    pt: "Login realizado com sucesso",
    en: "Login successful",
  },
  "login.successDescription": {
    pt: "Redirecionando...",
    en: "Redirecting...",
  },
  "login.errorTitle": {
    pt: "Erro no login",
    en: "Login error",
  },
  "login.keepSignedIn": {
    pt: "Manter-me conectado por 7 dias",
    en: "Keep me signed in for 7 days",
  },
  "login.emailRequired": {
    pt: "Esse campo é obrigatório",
    en: "This field is mandatory",
  },
  "login.passwordRequired": {
    pt: "Esse campo é obrigatório",
    en: "This field is mandatory",
  },

  // Session lifecycle
  "session.expiredTitle": {
    pt: "Sessão expirada",
    en: "Session expired",
  },
  "session.expiredDescription": {
    pt: "Sua sessão expirou. Por favor, entre novamente.",
    en: "Your session has expired. Please sign in again.",
  },

  // Forgot Password
  "forgotPassword.title": {
    pt: "Redefinir Senha",
    en: "Reset Password",
  },
  "forgotPassword.subtitle": {
    pt: "Digite seu e-mail para receber um link de redefinição",
    en: "Enter your email to receive a reset link",
  },
  "forgotPassword.email": {
    pt: "E-mail",
    en: "Email",
  },
  "forgotPassword.emailPlaceholder": {
    pt: "seu@email.com",
    en: "your@email.com",
  },
  "forgotPassword.sending": {
    pt: "Enviando...",
    en: "Sending...",
  },
  "forgotPassword.submit": {
    pt: "Enviar Link de Redefinição",
    en: "Send Reset Link",
  },
  "forgotPassword.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "forgotPassword.successTitle": {
    pt: "Email enviado",
    en: "Email sent",
  },
  "forgotPassword.successDescription": {
    pt: "Verifique sua caixa de entrada para instruções de redefinição de senha.",
    en: "Check your inbox for password reset instructions.",
  },
  "forgotPassword.successMessage": {
    pt: "Email de redefinição enviado!",
    en: "Reset email sent!",
  },
  "forgotPassword.successDetails": {
    pt: "Por favor, verifique sua caixa de entrada para instruções sobre como redefinir sua senha.",
    en: "Please check your inbox for instructions on how to reset your password.",
  },
  "forgotPassword.errorTitle": {
    pt: "Erro",
    en: "Error",
  },
  "forgotPassword.emailRequired": {
    pt: "Por favor, informe seu endereço de e-mail",
    en: "Please enter your email address",
  },

  // Reset Password
  "resetPassword.title": {
    pt: "Redefinir Senha",
    en: "Reset Password",
  },
  "resetPassword.subtitle": {
    pt: "Digite sua nova senha",
    en: "Enter your new password",
  },
  "resetPassword.initializing": {
    pt: "Inicializando redefinição de senha...",
    en: "Initializing password reset...",
  },
  "resetPassword.resettingFor": {
    pt: "Redefinindo senha para:",
    en: "Resetting password for:",
  },
  "resetPassword.newPassword": {
    pt: "Nova Senha",
    en: "New Password",
  },
  "resetPassword.confirmPassword": {
    pt: "Confirmar Nova Senha",
    en: "Confirm New Password",
  },
  "resetPassword.resetting": {
    pt: "Redefinindo Senha...",
    en: "Resetting Password...",
  },
  "resetPassword.submit": {
    pt: "Redefinir Senha",
    en: "Reset Password",
  },
  "resetPassword.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "resetPassword.successTitle": {
    pt: "Senha redefinida com sucesso",
    en: "Password reset successful",
  },
  "resetPassword.successDescription": {
    pt: "Redirecionando para a página de login...",
    en: "Redirecting to login page...",
  },
  "resetPassword.successMessage": {
    pt: "Sua senha foi redefinida com sucesso!",
    en: "Your password has been reset successfully!",
  },
  "resetPassword.successDetails": {
    pt: "Redirecionando para a página de login...",
    en: "Redirecting to login page...",
  },
  "resetPassword.errorTitle": {
    pt: "Erro",
    en: "Error",
  },
  "resetPassword.passwordRequired": {
    pt: "Por favor, informe uma nova senha",
    en: "Please enter a new password",
  },
  "resetPassword.passwordMismatch": {
    pt: "As senhas não coincidem",
    en: "Passwords do not match",
  },
  "resetPassword.passwordMinLength": {
    pt: "A senha deve ter pelo menos 8 caracteres",
    en: "Password must be at least 8 characters long",
  },
  "resetPassword.invalidLink": {
    pt: "Link de redefinição inválido. Faltando session_id ou session_secret.",
    en: "Invalid reset link. Missing session_id or session_secret.",
  },
  "resetPassword.noResetFound": {
    pt: "Nenhuma redefinição de senha pendente encontrada.",
    en: "No pending password reset found.",
  },

  // Account Holder Selection
  "accountHolder.loading": {
    pt: "Carregando organizações...",
    en: "Loading organizations...",
  },
  "accountHolder.noHolders": {
    pt: "Nenhuma organização ativa encontrada.",
    en: "No active organizations found.",
  },
  "accountHolder.title": {
    pt: "Selecione a organização",
    en: "Select Organization",
  },
  "accountHolder.subtitle": {
    pt: "Escolha a organização que deseja acessar:",
    en: "Choose the organization you want to access:",
  },
  "accountHolder.taxId": {
    pt: "Tax ID:",
    en: "Tax ID:",
  },
  "accountHolder.legalRepresentative": {
    pt: "Legal Representative:",
    en: "Legal Representative:",
  },
  "accountHolder.status": {
    pt: "ativo",
    en: "active",
  },
  "accountHolder.error": {
    pt: "Erro:",
    en: "Error:",
  },
  "accountHolder.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "accountHolder.noSessionId": {
    pt: "Nenhum ID de sessão encontrado. Por favor, faça login novamente.",
    en: "No session ID found. Please log in again.",
  },

  // Account Selection
  "account.loading": {
    pt: "Carregando contas...",
    en: "Loading accounts...",
  },
  "account.noAccounts": {
    pt: "Nenhuma conta ativa encontrada para esta organização.",
    en: "No active accounts found for this organization.",
  },
  "account.title": {
    pt: "Selecione a conta",
    en: "Select Account",
  },
  "account.subtitle": {
    pt: "Escolha a conta que deseja acessar:",
    en: "Choose the account you want to access:",
  },
  "account.type": {
    pt: "Tipo:",
    en: "Type:",
  },
  "account.back": {
    pt: "Voltar",
    en: "Back",
  },
  "account.status": {
    pt: "ativo",
    en: "active",
  },
  "account.error": {
    pt: "Erro:",
    en: "Error:",
  },
  "account.backToHolderSelection": {
    pt: "Voltar para a seleção de organização",
    en: "Back to Organization Selection",
  },
  "account.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "account.createAccount": {
    pt: "Criar conta",
    en: "Create account",
  },

  // Account Creation
  "accountCreate.title": {
    pt: "Criar conta",
    en: "Create account",
  },
  "accountCreate.subtitle": {
    pt: "A nova conta será criada nesta organização.",
    en: "The new account will be created under this organization.",
  },
  "accountCreate.nameLabel": {
    pt: "Nome da conta",
    en: "Account name",
  },
  "accountCreate.namePlaceholder": {
    pt: "Ex: Fundo Growth",
    en: "Ex: Growth Fund",
  },
  "accountCreate.uniqueIdLabel": {
    pt: "Identificador único da conta",
    en: "Account unique identifier",
  },
  "accountCreate.submit": {
    pt: "Criar conta",
    en: "Create account",
  },
  "accountCreate.creating": {
    pt: "Criando...",
    en: "Creating...",
  },
  "accountCreate.back": {
    pt: "Voltar",
    en: "Back",
  },
  "accountCreate.nameRequired": {
    pt: "Informe um nome para a conta.",
    en: "Enter a name for the account.",
  },
  "accountCreate.nameLength": {
    pt: "O nome deve ter entre 3 e 36 caracteres.",
    en: "The name must be between 3 and 36 characters.",
  },
  "accountCreate.nameHtml": {
    pt: "O nome não pode conter tags HTML.",
    en: "The name cannot contain HTML tags.",
  },
  "accountCreate.uniqueIdRequired": {
    pt: "Informe um identificador único para a conta.",
    en: "Enter a unique identifier for the account.",
  },
  "accountCreate.confirmTitle": {
    pt: "Criar esta conta?",
    en: "Create this account?",
  },
  "accountCreate.confirmBody": {
    pt: "{name} será criada com o identificador único {uniqueId}. Contas não podem ser removidas pelo console.",
    en: "{name} will be created with the unique identifier {uniqueId}. Accounts cannot be removed from the console.",
  },
  "accountCreate.confirmBodyUnderHolder": {
    pt: "{name} será criada em {holder} com o identificador único {uniqueId}. Contas não podem ser removidas pelo console.",
    en: "{name} will be created under {holder} with the unique identifier {uniqueId}. Accounts cannot be removed from the console.",
  },
  "accountCreate.confirmCancel": {
    pt: "Cancelar",
    en: "Cancel",
  },
  "accountCreate.confirmAction": {
    pt: "Criar conta",
    en: "Create account",
  },
  "accountCreate.createdTitle": {
    pt: "Conta criada",
    en: "Account created",
  },
  "accountCreate.createdBody": {
    pt: "{name} está ativa e pronta para uso.",
    en: "{name} is active and ready to use.",
  },
  "accountCreate.createdUnreadable": {
    pt: "A conta foi criada, mas não foi possível ler a resposta. Confira a lista de contas antes de criar outra.",
    en: "The account was created, but the response could not be read. Check the account list before creating another one.",
  },
  "accountCreate.ambiguousFailure": {
    pt: "A requisição pode ter chegado ao servidor. Confira a lista de contas antes de tentar novamente.",
    en: "The request may have reached the server. Check the account list before trying again.",
  },
  "accountCreate.goToAccounts": {
    pt: "Ir para a lista de contas",
    en: "Go to account list",
  },
  "accountCreate.permissionDenied": {
    pt: "Suas funções não permitem criar contas. Solicite a um administrador a permissão de criação de contas.",
    en: "Your roles cannot create accounts. Ask an administrator for the account creation permission.",
  },
  "accountCreate.noHolder": {
    pt: "Nenhuma organização está em escopo, então não há onde criar a conta.",
    en: "No organization is in scope, so there is nothing to create the account under.",
  },
  "accountCreate.createError": {
    pt: "Não foi possível criar a conta",
    en: "Could not create the account",
  },

  // Sidebar
  "sidebar.back": {
    pt: "Voltar",
    en: "Back",
  },
  "sidebar.menu": {
    pt: "Menu",
    en: "Menu",
  },
  "sidebar.openMenu": {
    pt: "Abrir menu",
    en: "Open menu",
  },
  "sidebar.dashboard": {
    pt: "Início",
    en: "Home",
  },
  "sidebar.access": {
    pt: "Acessos",
    en: "Access",
  },
  "sidebar.integrations": {
    pt: "Integrações",
    en: "Integrations",
  },
  // Legacy key for backward compatibility
  "sidebar.projects": {
    pt: "Integrações",
    en: "Integrations",
  },
  "sidebar.investors": {
    pt: "Investidores",
    en: "Investors",
  },
  "sidebar.portfolio": {
    pt: "Portfólio",
    en: "Portfolio",
  },
  "sidebar.pricing": {
    pt: "Precificação",
    en: "Pricing",
  },
  "portfolio.title": {
    pt: "Portfólios",
    en: "Portfolios",
  },
  "portfolio.subtitle": {
    pt: "Acompanhe os portfólios e posições externas da conta",
    en: "Track the account's portfolios and external positions",
  },
  "portfolio.detailSubtitle": {
    pt: "Posições, valor e operações externas",
    en: "Positions, value and external operations",
  },
  "portfolio.new": {
    pt: "Novo portfólio",
    en: "New portfolio",
  },
  "portfolio.newTitle": {
    pt: "Criar portfólio",
    en: "Create portfolio",
  },
  "portfolio.newDescription": {
    pt: "Um portfólio agrupa as posições de um investidor dentro desta conta.",
    en: "A portfolio groups an investor's positions within this account.",
  },
  "portfolio.name": {
    pt: "Nome",
    en: "Name",
  },
  "portfolio.owner": {
    pt: "Investidor titular",
    en: "Owning investor",
  },
  "portfolio.ownerPlaceholder": {
    pt: "Selecione o investidor",
    en: "Select the investor",
  },
  "portfolio.nameRequired": {
    pt: "Informe o nome do portfólio",
    en: "Portfolio name is required",
  },
  "portfolio.ownerRequired": {
    pt: "Selecione o investidor titular",
    en: "Select the owning investor",
  },
  "portfolio.uniqueIdLabel": {
    pt: "Identificador único do portfólio",
    en: "Portfolio unique identifier",
  },
  "portfolio.uniqueIdRequired": {
    pt: "Informe um identificador único para o portfólio.",
    en: "Enter a unique identifier for the portfolio.",
  },
  "portfolio.search": {
    pt: "Buscar portfólios",
    en: "Search portfolios",
  },
  "portfolio.empty": {
    pt: "Nenhum portfólio ainda. Crie o primeiro para começar a acompanhar posições.",
    en: "No portfolios yet. Create the first one to start tracking positions.",
  },
  "portfolio.since": {
    pt: "Criado em",
    en: "Created",
  },
  "portfolio.created": {
    pt: "Portfólio criado",
    en: "Portfolio created",
  },
  "portfolio.creating": {
    pt: "Criando...",
    en: "Creating...",
  },
  "portfolio.create": {
    pt: "Criar",
    en: "Create",
  },
  "portfolio.cancel": {
    pt: "Cancelar",
    en: "Cancel",
  },
  "portfolio.loadFailed": {
    pt: "Não foi possível carregar os portfólios",
    en: "Could not load portfolios",
  },
  "portfolio.remove": {
    pt: "Remover da lista",
    en: "Remove from list",
  },
  "portfolio.removed": {
    pt: "Portfólio removido da lista",
    en: "Portfolio removed from the list",
  },
  "portfolio.restored": {
    pt: "Portfólio restaurado",
    en: "Portfolio restored",
  },
  "portfolio.removeHint": {
    pt: "Remove o portfólio da lista e bloqueia novas operações nele. Pode ser restaurado.",
    en: "Removes the portfolio from the list and blocks new trades on it. It can be restored.",
  },
  "portfolio.removeConfirmTitle": {
    pt: "Remover portfólio da lista?",
    en: "Remove portfolio from the list?",
  },
  "portfolio.removeConfirmDescription": {
    pt: "\"{name}\" sai da lista e deixa de aceitar novas operações, para todos os usuários da conta. Você pode restaurá-lo depois em \"Mostrar removidos\".",
    en: "\"{name}\" leaves the list and stops accepting new trades, for every user of the account. You can restore it later under \"Show removed\".",
  },
  "portfolio.showRemoved": {
    pt: "Mostrar removidos",
    en: "Show removed",
  },
  "portfolio.hideRemoved": {
    pt: "Ocultar removidos",
    en: "Hide removed",
  },
  "portfolio.allStatusesNote": {
    pt: "Listando todos os status, incluindo portfólios removidos e encerrados.",
    en: "Listing every status, including removed and closed portfolios.",
  },
  "portfolio.restore": {
    pt: "Restaurar",
    en: "Restore",
  },
  "portfolio.restoreHint": {
    pt: "Devolve o portfólio à lista e volta a aceitar operações.",
    en: "Returns the portfolio to the list and accepts trades again.",
  },
  "portfolio.listedEmpty": {
    pt: "Nenhum portfólio nesta lista. Portfólios removidos ou encerrados podem existir — use \"Mostrar removidos\" para ver todos os status.",
    en: "No portfolios in this list. Removed or closed portfolios may still exist — use \"Show removed\" to see every status.",
  },
  "portfolio.listIncomplete": {
    pt: "Esta lista está incompleta: parte dos portfólios não pôde ser carregada.",
    en: "This list is incomplete — some portfolios could not be loaded.",
  },
  "portfolio.removedNote": {
    pt: "Removido da lista: fica bloqueado para novas operações e pode ser restaurado.",
    en: "Removed from the list: blocked for new trades, and can be restored.",
  },
  "portfolio.frozenNote": {
    pt: "Congelado pela Veltiz. Não aceita novas operações e só a Veltiz pode reativá-lo.",
    en: "Frozen by Veltiz. It takes no new trades, and only Veltiz can reactivate it.",
  },
  "portfolio.closedNote": {
    pt: "Encerrado definitivamente. Não aceita novas operações e não pode ser restaurado.",
    en: "Permanently closed. It takes no new trades and cannot be restored.",
  },
  "portfolio.tradesBlocked": {
    pt: "Este portfólio não está ativo e não aceita novas operações.",
    en: "This portfolio is not active and does not accept new trades.",
  },
  "portfolio.snapshotsBlocked": {
    pt: "Este portfólio não está ativo e não aceita novas posições declaradas.",
    en: "This portfolio is not active and does not accept new position declarations.",
  },
  "portfolio.noSearchMatch": {
    pt: "Nenhum portfólio corresponde a esta busca.",
    en: "No portfolio matches this search.",
  },
  "portfolio.statusUpdateFailed": {
    pt: "Não foi possível atualizar o status do portfólio",
    en: "Could not update the portfolio status",
  },
  "portfolio.noAccountScope": {
    pt: "Selecione uma conta para acessar os portfólios",
    en: "Select an account to access portfolios",
  },
  "portfolio.nav": {
    pt: "Valor do portfólio (NAV)",
    en: "Portfolio value (NAV)",
  },
  "portfolio.positionCount": {
    pt: "Posições ativas",
    en: "Active positions",
  },
  "portfolio.tab.positions": {
    pt: "Posições",
    en: "Positions",
  },
  "portfolio.tab.operations": {
    pt: "Operações",
    en: "Operations",
  },

  // Portfolio — CSV import (upload, scan, parse, results)
  "portfolio.import.open.trade-parse-request": {
    pt: "Enviar arquivo de operações",
    en: "Upload trade file",
  },
  "portfolio.import.open.position-parse-request": {
    pt: "Enviar arquivo de posições",
    en: "Upload position file",
  },
  "portfolio.import.title.trade-parse-request": {
    pt: "Importar operações",
    en: "Import trades",
  },
  "portfolio.import.title.position-parse-request": {
    pt: "Importar posições",
    en: "Import positions",
  },
  "portfolio.import.close": {
    pt: "Fechar o painel de importação",
    en: "Close the import panel",
  },
  "portfolio.import.typeHint.trade-parse-request": {
    pt: "Cada linha vira uma operação externa registrada neste portfólio.",
    en: "Each row becomes an external trade recorded in this portfolio.",
  },
  "portfolio.import.typeHint.position-parse-request": {
    pt: "Cada linha declara a quantidade de um ativo em uma data.",
    en: "Each row declares an asset's quantity on a given date.",
  },
  "portfolio.import.choose": {
    pt: "Clique ou solte um arquivo aqui",
    en: "Click or drop a file here",
  },
  "portfolio.import.dropHere": {
    pt: "Solte o arquivo aqui para selecioná-lo",
    en: "Drop the file here to select it",
  },
  "portfolio.import.clearFile": {
    pt: "Remover o arquivo escolhido",
    en: "Remove the chosen file",
  },
  "portfolio.import.csvOnly": {
    pt: "Somente CSV, até 30 MiB.",
    en: "CSV only, up to 30 MiB.",
  },
  "portfolio.import.start": {
    pt: "Enviar",
    en: "Upload",
  },
  "portfolio.import.uploading": {
    pt: "Enviando…",
    en: "Uploading…",
  },
  "portfolio.import.startOver": {
    pt: "Recomeçar",
    en: "Start over",
  },
  "portfolio.import.uploadAgain": {
    pt: "Enviar novamente",
    en: "Upload again",
  },
  "portfolio.import.importAnother": {
    pt: "Importar outro arquivo",
    en: "Import another file",
  },
  "portfolio.import.reject.not-csv": {
    pt: "Somente arquivos CSV. Planilhas XLSX e PDF ainda não são aceitos — exporte como CSV.",
    en: "CSV files only. XLSX and PDF are not accepted yet — export as CSV instead.",
  },
  "portfolio.import.reject.too-large": {
    pt: "O arquivo passa de 30 MiB. Divida-o em partes menores.",
    en: "The file is over 30 MiB. Split it into smaller files.",
  },
  "portfolio.import.reject.empty-file": {
    pt: "O arquivo está vazio.",
    en: "The file is empty.",
  },
  "portfolio.import.reject.not-utf8": {
    pt: "Não foi possível ler o arquivo como texto UTF-8. Salve-o novamente com codificação UTF-8.",
    en: "The file could not be read as UTF-8 text. Save it again with UTF-8 encoding.",
  },
  "portfolio.import.stage.scanning": {
    pt: "Verificando o arquivo",
    en: "Checking the file",
  },
  "portfolio.import.stage.starting": {
    pt: "Iniciando o processamento",
    en: "Starting the parse",
  },
  "portfolio.import.stage.parsing": {
    pt: "Processando as linhas",
    en: "Parsing the rows",
  },
  "portfolio.import.stageHint": {
    pt: "Isso leva alguns instantes. Você pode alternar entre as abas — apenas permaneça na página deste portfólio.",
    en: "This takes a moment. You can switch between the tabs — just stay on this portfolio's page.",
  },
  "portfolio.import.stalledHint": {
    pt: "Isso está demorando mais do que o esperado. Não falhou — nossos sistemas ainda podem concluí-lo automaticamente. Você pode continuar aguardando ou recomeçar; recomeçar não cancela esta importação, apenas inicia uma nova.",
    en: "This is taking longer than expected. It has not failed — our systems can still complete it automatically. You can keep waiting, or start over; starting over does not cancel this import, it begins a new one.",
  },
  "portfolio.import.scanCheckFailed": {
    pt: "Não foi possível verificar a situação do arquivo enviado.",
    en: "Could not check the uploaded file's status.",
  },
  "portfolio.import.scanTimedOut": {
    pt: "A verificação de segurança do arquivo ainda está em andamento após dez minutos. Esta página parou de verificar automaticamente. Tentar novamente verifica de novo e continua acompanhando; recomeçar não cancela esta importação, apenas inicia uma nova.",
    en: "The uploaded file's security scan is still running after ten minutes. This page stopped checking automatically. Retry checks again and keeps watching; starting over does not cancel this import, it begins a new one.",
  },
  "portfolio.import.quarantined": {
    pt: "O arquivo foi bloqueado pela verificação de segurança e não será processado. Envie outro arquivo.",
    en: "The file was blocked by the security scan and will not be parsed. Upload a different file.",
  },
  "portfolio.import.notStored": {
    pt: "O arquivo enviado não pôde ser armazenado. Envie-o novamente.",
    en: "The uploaded file could not be stored. Upload it again.",
  },
  "portfolio.import.parseCheckFailed": {
    pt: "Não foi possível verificar o andamento do processamento.",
    en: "Could not check the parse progress.",
  },
  "portfolio.import.parseTimedOut": {
    pt: "O processamento das linhas ainda está em andamento após dez minutos. Esta página parou de verificar automaticamente. Tentar novamente verifica de novo e continua acompanhando; recomeçar não cancela esta importação, apenas inicia uma nova.",
    en: "Parsing the rows is still running after ten minutes. This page stopped checking automatically. Retry checks again and keeps watching; starting over does not cancel this import, it begins a new one.",
  },
  "portfolio.import.startFailed": {
    pt: "Não foi possível iniciar o processamento do arquivo.",
    en: "Could not start parsing the file.",
  },
  "portfolio.import.uploadFailed": {
    pt: "Não foi possível enviar o arquivo.",
    en: "Could not upload the file.",
  },
  "portfolio.import.duplicateFile": {
    pt: "Este arquivo já foi enviado antes e não pode ser recuperado por esta página. Salve o arquivo novamente com qualquer alteração no conteúdo (acrescente ou edite um caractere) e envie essa cópia.",
    en: "This exact file was uploaded before and cannot be recovered by this page. Save the file again with any change to its contents (add or edit a character) and upload that copy.",
  },
  "portfolio.import.uploadUnconfirmed": {
    pt: "O arquivo já consta como enviado, mas não foi possível confirmar. Envie novamente para verificar.",
    en: "The file appears to be uploaded already, but confirming it failed. Upload again to check.",
  },
  "portfolio.import.uploadNotAdoptable": {
    pt: "Não foi encontrada nenhuma cópia armazenada e utilizável deste arquivo. Envie-o novamente para armazená-lo como uma nova cópia.",
    en: "No usable stored copy of this exact file was found. Upload it again to store it as a new copy.",
  },
  "portfolio.import.startUnconfirmed": {
    pt: "O processamento já consta como iniciado, mas não foi possível confirmar. Tente novamente para verificar.",
    en: "Parsing appears to be started already, but confirming it failed. Retry to check.",
  },
  "portfolio.import.importedElsewhere": {
    pt: "Uma importação deste arquivo já foi iniciada em outro portfólio. Não é possível importá-lo neste portfólio.",
    en: "An import of this file was already started in a different portfolio. It cannot be imported into this one.",
  },
  "portfolio.import.replayNotice": {
    pt: "Este arquivo já havia sido processado antes. Os resultados abaixo são desse processamento registrado; nenhuma linha foi criada de novo. Para importar este conteúdo de novo, salve o arquivo com qualquer alteração no conteúdo (acrescente ou edite um caractere) e envie essa cópia.",
    en: "This file had already been parsed before. The results below are from that recorded run; no row was created again. To import this content again, save the file with any change to its contents (add or edit a character) and upload that copy.",
  },
  "portfolio.import.resultsFailed": {
    pt: "Não foi possível carregar o resultado do processamento.",
    en: "Could not load the parse results.",
  },
  "portfolio.import.rowsFailed": {
    pt: "Não foi possível carregar as linhas recusadas.",
    en: "Could not load the rejected rows.",
  },
  "portfolio.import.rejectedTruncated": {
    pt: "Mostrando as primeiras {shown} de {total} linhas recusadas.",
    en: "Showing the first {shown} of {total} rejected rows.",
  },
  "portfolio.import.noRowsMapped": {
    pt: "Nenhuma linha pôde ser interpretada. Verifique os títulos das colunas e envie novamente.",
    en: "No row could be read. Check the column headers and upload the file again.",
  },
  // A `failed` request whose reasons name the OTHER parse-request type — literal
  // per-type keys, never composed from a request type at runtime, matching this file's
  // own convention: a missing key renders as the raw key on screen, so a third
  // `ParseRequestType` failing to add its pair here fails loudly instead of silently.
  //
  // The backend also sends a localized `message` on the same reason (see
  // `ParsedRowReason` in `api/parse-request.ts`). This console deliberately
  // never renders it here — these two strings are hand-written instead, because only the
  // console can add the "switch and continue" call to action; the backend's message can
  // only describe the mismatch, not offer the fix. This is a considered choice, not an
  // oversight: the two copies can drift out of sync with each other, and nothing catches
  // that automatically.
  "portfolio.import.shapeMismatch.position-parse-request": {
    pt: "As colunas deste arquivo correspondem a uma lista de posições, não a operações. Importe-o como posições.",
    en: "This file's columns read as a list of positions, not trades. Import it as positions instead.",
  },
  "portfolio.import.shapeMismatch.trade-parse-request": {
    pt: "As colunas deste arquivo correspondem a uma lista de operações, não a posições. Importe-o como operações.",
    en: "This file's columns read as a list of trades, not as positions. Import it as trades instead.",
  },
  // "Continue", not "Import": clicking this returns to the pre-dispatch form under the
  // new type instead of importing anything on its own — the user still has to press
  // Start again. "Import as…" promised a completed action a second click was still
  // needed for.
  "portfolio.import.switchTo.position-parse-request": {
    pt: "Continuar como posições",
    en: "Continue as positions",
  },
  "portfolio.import.switchTo.trade-parse-request": {
    pt: "Continuar como operações",
    en: "Continue as trades",
  },
  "portfolio.import.created": {
    pt: "Linhas registradas",
    en: "Rows recorded",
  },
  "portfolio.import.rejected": {
    pt: "Linhas recusadas",
    en: "Rows rejected",
  },
  "portfolio.import.outcome.success": {
    pt: "Todas as linhas foram registradas.",
    en: "Every row was recorded.",
  },
  "portfolio.import.outcome.partial": {
    pt: "Parte das linhas foi registrada. As demais estão listadas abaixo.",
    en: "Some rows were recorded. The rest are listed below.",
  },
  "portfolio.import.outcome.rejected": {
    pt: "Nenhuma linha foi registrada. Os motivos estão listados abaixo.",
    en: "No row was recorded. The reasons are listed below.",
  },
  "portfolio.import.bookingContinues": {
    pt: "As posições e o NAV atualizam conforme cada linha é registrada, normalmente em segundos. Esta página atualiza sozinha.",
    en: "Positions and NAV update as each row books, usually within seconds. This page refreshes by itself.",
  },
  "portfolio.import.rejectedTitle": {
    pt: "Linhas recusadas",
    en: "Rejected rows",
  },
  "portfolio.import.rejectedHint": {
    pt: "Corrija estas linhas no seu arquivo e importe-as novamente.",
    en: "Fix these rows in your file and import them again.",
  },
  "portfolio.import.col.row": {
    pt: "Linha",
    en: "Row",
  },
  "portfolio.import.col.reason": {
    pt: "Motivo",
    en: "Reason",
  },
  "portfolio.import.referenceTimeLabel": {
    pt: "Data da posição",
    en: "Position date",
  },
  "portfolio.import.referenceTimeHint": {
    pt: "Vale para o arquivo inteiro: um arquivo de posições não tem coluna de data própria.",
    en: "Applies to the whole file: a holdings file carries no date column of its own.",
  },
  "portfolio.import.referenceTimeInvalid": {
    pt: "Escolha uma data da posição válida.",
    en: "Choose a valid position date.",
  },
  // The confirmation step a holdings import starts from. Only the position request type
  // renders it, so these keys carry no per-type suffix — see
  // `IMPORT_TYPE_CONFIG.declaresCompleteness`.
  "portfolio.import.confirmTitle": {
    pt: "Iniciar a importação",
    en: "Start the import",
  },
  "portfolio.import.confirmDescription": {
    pt: "Confirme a data das posições e como este arquivo é aplicado ao portfólio.",
    en: "Confirm the date of the positions and how this file is applied to the portfolio.",
  },
  "portfolio.import.completeLabel": {
    pt: "Este arquivo é o extrato completo do portfólio",
    en: "This file is the portfolio's complete statement",
  },
  // Says what checking the box does to positions the file leaves out, and what leaving it
  // unchecked does instead. The destructive half comes first: it is the part the user
  // cannot undo, and the part a checkbox label alone cannot state.
  "portfolio.import.completeHint": {
    pt: "Toda posição que o arquivo não lista é encerrada nessa mesma data das posições. Deixe desmarcado para somente adicionar e atualizar as posições que o arquivo lista.",
    en: "Every position the file does not list is closed as of that same position date. Leave it unchecked to only add and update the positions the file lists.",
  },
  "portfolio.import.confirm": {
    pt: "Iniciar importação",
    en: "Start import",
  },
  // Shown only where the settled request itself reports the declaration was made. `{date}`
  // is the position date the request was dispatched with.
  "portfolio.import.completeApplied": {
    pt: "O arquivo foi aplicado como extrato completo do portfólio em {date}. As posições que ele não lista foram encerradas.",
    en: "The file was applied as the portfolio's complete statement on {date}. The positions it does not list were closed.",
  },
  // Shown under the not-applied sentence only on a `success` request: the declaration was
  // withdrawn with no rejected row to correct, and a re-import of the same file adopts
  // this same request, so it will not declare again.
  "portfolio.import.completeNotRetryable": {
    pt: "Importar este arquivo novamente não repetirá a declaração.",
    en: "Importing this file again will not repeat the declaration.",
  },
  "portfolio.import.templateLink": {
    pt: "Baixar arquivo de exemplo",
    en: "Download example file",
  },
  "portfolio.import.templateHint.trade-parse-request": {
    pt: "Um exemplo preenchido com as colunas e o formato aceitos por esta importação. Use-o como modelo para preencher à mão, ou entregue-o a uma ferramenta de IA junto com o extrato da sua corretora para convertê-lo a este formato.",
    en: "A filled-in example of the columns and format this import accepts. Use it as a template to fill in by hand, or hand it to an AI tool along with your broker's export to convert it into this format.",
  },
  "portfolio.import.templateHint.position-parse-request": {
    pt: "Um exemplo preenchido com as colunas e o formato aceitos por esta importação. O arquivo não tem coluna de data — todas as linhas usam a Data da posição que você confirma ao iniciar. Use-o como modelo para preencher à mão, ou entregue-o a uma ferramenta de IA junto com o extrato da sua corretora para convertê-lo a este formato.",
    en: "A filled-in example of the columns and format this import accepts. The file carries no date column — every row uses the Position date you confirm when you start. Use it as a template to fill in by hand, or hand it to an AI tool along with your broker's export to convert it into this format.",
  },
  "portfolio.positionsEmpty": {
    pt: "Nenhuma posição ativa. Registre operações externas para começar.",
    en: "No active positions. Record external trades to get started.",
  },
  // The empty table while the book is still settling — see `useBookingStatus` and
  // `emptyPositionsCopy` in `booking-label.ts`, which picks one of the three counts below
  // and appends the state's own tail (this settling one, or the shared `slow`/timed-out
  // strings further down). Same `{count}` + `...One` convention as `previewResultCount`.
  "portfolio.positionsBookingCount": {
    pt: "Ainda não há posições: {count} operações enviadas ainda estão sendo registradas.",
    en: "No positions yet: {count} recorded trades are still booking.",
  },
  "portfolio.positionsBookingCountOne": {
    pt: "Ainda não há posições: 1 operação enviada ainda está sendo registrada.",
    en: "No positions yet: 1 recorded trade is still booking.",
  },
  "portfolio.positionsSnapshotsSettling": {
    pt: "Ainda não há posições: {count} declarações ainda estão em processamento.",
    en: "No positions yet: {count} declarations are still settling.",
  },
  "portfolio.positionsSnapshotsSettlingOne": {
    pt: "Ainda não há posições: 1 declaração ainda está em processamento.",
    en: "No positions yet: 1 declaration is still settling.",
  },
  // Count-aware, same `{count}` / `...One` convention as `positionsBookingCount` above
  // and the marker's own `portfolio.booking.imports` — see `emptyPositionsCopy`, which
  // now routes this branch through `bookingCountLabel` so two imports running never
  // shows as the marker's "2 imports running" beside this card's uncounted singular.
  "portfolio.positionsImportRunning": {
    pt: "Ainda não há posições: {count} importações ainda em andamento.",
    en: "No positions yet: {count} imports still running.",
  },
  "portfolio.positionsImportRunningOne": {
    pt: "Ainda não há posições: 1 importação ainda está em andamento.",
    en: "No positions yet: 1 import is still running.",
  },
  // The timed-out tail for the empty-table copy above — the poll has stopped, so unlike
  // the settling and slow tails (`portfolio.booking.tail` / `.slow`, reused here) this one
  // must not promise the page will keep refreshing on its own.
  "portfolio.positionsCheckStopped": {
    pt: "Esta página parou de verificar automaticamente. Use Verificar novamente acima.",
    en: "This page stopped checking automatically. Use Check again above.",
  },
  // The empty table when the booking check itself failed — see `useBookingStatus`'s
  // `isUnknown`. Distinct from `positionsEmpty`: the page cannot say "no positions" when
  // it does not know whether some are still booking.
  "portfolio.positionsUnknown": {
    pt: "Nenhuma posição listada. Não foi possível verificar se alguma ainda está sendo registrada.",
    en: "No positions are listed. Whether any are still booking could not be checked.",
  },
  // Portfolio — booking marker (`BookingMarker`, `useBookingStatus`). "NAV" and
  // "operações" are the words the NAV card and the blotter already use.
  "portfolio.booking.short": {
    pt: "Atualizando",
    en: "Updating",
  },
  "portfolio.booking.shortSlow": {
    pt: "Ainda atualizando",
    en: "Still updating",
  },
  "portfolio.booking.shortTimedOut": {
    pt: "Verificação interrompida",
    en: "Stopped checking",
  },
  "portfolio.booking.shortUnknown": {
    pt: "Não verificado",
    en: "Unverified",
  },
  // The incomplete check's chip: the running imports could not be verified, and no poll
  // is running — so never "Updating", which promises a refresh this state does not make.
  "portfolio.booking.shortIncomplete": {
    pt: "Não verificado",
    en: "Unverified",
  },
  "portfolio.booking.trades": {
    pt: "{count} operações ainda sendo registradas",
    en: "{count} trades still booking",
  },
  "portfolio.booking.tradesOne": {
    pt: "1 operação ainda sendo registrada",
    en: "1 trade still booking",
  },
  "portfolio.booking.snapshots": {
    pt: "{count} declarações em processamento",
    en: "{count} declarations settling",
  },
  "portfolio.booking.snapshotsOne": {
    pt: "1 declaração em processamento",
    en: "1 declaration settling",
  },
  "portfolio.booking.imports": {
    pt: "{count} importações em andamento",
    en: "{count} imports running",
  },
  "portfolio.booking.importsOne": {
    pt: "1 importação em andamento",
    en: "1 import running",
  },
  // The one case none of the three counted legs can name: every count answered zero but
  // a read still carried a cursor, so the true count is an unproven lower bound, not a
  // proven settled book. Shown in place of the three counted parts above (marker) or the
  // three counted sentences above (empty table) whenever none of them applies — see
  // `bookingParts` in `BookingMarker.tsx` and `emptyPositionsCopy` in `booking-label.ts`.
  // No trailing period: both call sites punctuate it themselves, like the counted parts.
  "portfolio.booking.uncounted": {
    pt: "Linhas além da primeira página ainda podem estar sendo registradas",
    en: "Rows beyond the first page may still be booking",
  },
  // A running-import read failed this tick, so the running-import figure is missing
  // whatever that read would have returned. A caveat, not a count: appended as its own
  // sentence after whatever the book is known to be waiting on, or standing alone in
  // the incomplete state. Never a `+` suffix, which is what a cursor means, not a
  // failure — see `readBookingCounts` in `use-booking-status.ts`.
  "portfolio.booking.importsUnchecked": {
    pt: "Não foi possível verificar as importações em andamento",
    en: "Running imports could not be checked",
  },
  "portfolio.booking.tail": {
    pt: "As posições e o NAV atualizam conforme são registradas.",
    en: "Positions and NAV update as they book.",
  },
  "portfolio.booking.slow": {
    pt: "O registro está demorando mais que o normal. Os valores atualizam quando concluir; você pode sair desta página e voltar depois.",
    en: "Booking is taking longer than usual. The figures update when it completes; you can leave this page and come back.",
  },
  // The timed-out tail for the marker itself (full sentence, live announcement, and both
  // compact `aria-label`s) — unlike `.tail`/`.slow`, must never promise the page keeps
  // refreshing once the poll has genuinely stopped. Same "stopped checking automatically"
  // wording as the import poll's own timed-out copy (`portfolio.import.scanTimedOut` /
  // `.parseTimedOut`) and the empty-table sibling (`portfolio.positionsCheckStopped`).
  "portfolio.booking.stopped": {
    pt: "Esta página parou de verificar automaticamente. Use Verificar novamente para procurar novos registros.",
    en: "This page stopped checking automatically. Use Check again to look for new bookings.",
  },
  // The incomplete tail: the trade and snapshot reads proved nothing is settling, so no
  // poll runs — like `.stopped`, this must never promise the page refreshes on its own,
  // and it names Check again as the way to a verified answer.
  "portfolio.booking.incompleteTail": {
    pt: "Esta página não está verificando automaticamente. Use Verificar novamente para procurar importações em andamento.",
    en: "This page is not checking on its own. Use Check again to look for running imports.",
  },
  "portfolio.booking.unknown": {
    pt: "Não foi possível verificar se estes valores estão atualizados.",
    en: "Could not check whether these figures are up to date.",
  },
  "portfolio.booking.checkAgain": {
    pt: "Verificar novamente",
    en: "Check again",
  },
  "portfolio.tradesEmpty": {
    pt: "Nenhuma operação registrada ainda.",
    en: "No trades recorded yet.",
  },
  "portfolio.allocationTitle": {
    pt: "Alocação por classe",
    en: "Allocation by asset class",
  },
  "portfolio.allocationZeroNav": {
    pt: "O total deste portfólio é zero, portanto nenhuma classe tem uma parcela que possa ser medida.",
    en: "This portfolio's total is zero, so no asset class has a share that can be measured.",
  },
  "portfolio.allocationPending": {
    pt: "A alocação aparece quando houver preços para os ativos.",
    en: "Allocation appears once asset prices are available.",
  },
  "portfolio.breakdownNetOfShorts": {
    pt: "As parcelas são líquidas de posições vendidas, portanto uma parcela pode ser negativa ou maior que 100%. Nenhum gráfico é exibido, porque um gráfico não mostra uma parcela negativa.",
    en: "Shares are net of short positions, so a share can be negative or more than 100%. No chart is shown, because a chart cannot show a negative share.",
  },
  "portfolio.currencyAllocationTitle": {
    pt: "Alocação por moeda",
    en: "Allocation by currency",
  },
  "portfolio.currencyAllocationUnknown": {
    pt: "Moeda desconhecida",
    en: "Unknown currency",
  },
  "portfolio.currencyAllocationUnknownWeight": {
    pt: "fora do total",
    en: "not in the total",
  },
  "portfolio.currencyAllocationZeroNav": {
    pt: "O total deste portfólio é zero, portanto nenhuma moeda tem uma parcela que possa ser medida.",
    en: "This portfolio's total is zero, so no currency has a share that can be measured.",
  },
  "portfolio.currencyAllocationFxUnavailable": {
    pt: "A alocação por moeda aparece quando a taxa de câmbio estiver disponível.",
    en: "Currency allocation appears once the exchange rate is available.",
  },
  "portfolio.currencyAllocationPending": {
    pt: "A alocação por moeda aparece quando houver preços para os ativos.",
    en: "Currency allocation appears once asset prices are available.",
  },
  "portfolio.currencyAllocationUnreconciled": {
    pt: "A alocação por moeda não é exibida porque não foi possível conferi-la com o total deste portfólio.",
    en: "Currency allocation is not shown because it could not be checked against this portfolio's total.",
  },
  "portfolio.currencyAllocationUnknownOnly": {
    pt: "Nenhuma posição com valor tem moeda conhecida, portanto nenhuma moeda tem uma parcela que possa ser medida.",
    en: "No position with a value has a known currency, so no currency has a share that can be measured.",
  },
  "portfolio.class.debt": {
    pt: "Renda Fixa",
    en: "Fixed Income",
  },
  "portfolio.class.equity": {
    pt: "Ações",
    en: "Equities",
  },
  "portfolio.class.currency": {
    pt: "Moedas",
    en: "Currencies",
  },
  "portfolio.class.crypto": {
    pt: "Cripto",
    en: "Crypto",
  },
  "portfolio.class.fund": {
    pt: "Fundos",
    en: "Funds",
  },
  "portfolio.class.option": {
    pt: "Opções",
    en: "Options",
  },
  "portfolio.col.instrument": {
    pt: "Instrumento",
    en: "Instrument",
  },
  "portfolio.col.type": {
    pt: "Classe",
    en: "Class",
  },
  "portfolio.col.quantity": {
    pt: "Quantidade",
    en: "Quantity",
  },
  "portfolio.col.price": {
    pt: "Preço",
    en: "Price",
  },
  "portfolio.col.value": {
    pt: "Valor",
    en: "Value",
  },
  "portfolio.col.weight": {
    pt: "% do portfólio",
    en: "% of portfolio",
  },
  "portfolio.col.referenceTime": {
    pt: "Data da operação",
    en: "Trade date",
  },
  "portfolio.referenceTimeOfDay": {
    pt: "Hora (opcional)",
    en: "Time (optional)",
  },
  "portfolio.col.created": {
    pt: "Registrado em",
    en: "Recorded",
  },
  "portfolio.showCanceled": {
    pt: "Mostrar canceladas",
    en: "Show canceled",
  },
  "portfolio.col.side": {
    pt: "Tipo",
    en: "Side",
  },
  "portfolio.col.status": {
    pt: "Status",
    en: "Status",
  },
  "portfolio.addTrade": {
    pt: "Registrar operação",
    en: "Add trade",
  },
  "portfolio.addTradeTitle": {
    pt: "Registrar operação externa",
    en: "Record external trade",
  },
  "portfolio.addTradeDescription": {
    pt: "Operações executadas fora da Veltiz entram no ledger e atualizam a posição.",
    en: "Trades executed outside Veltiz are booked to the ledger and update the position.",
  },
  "portfolio.instrument": {
    pt: "Instrumento",
    en: "Instrument",
  },
  "portfolio.instrumentRequired": {
    pt: "Selecione um instrumento",
    en: "Select an instrument",
  },
  "portfolio.searchInstrument": {
    pt: "Busque por símbolo, nome ou ISIN",
    en: "Search by symbol, name or ISIN",
  },
  "portfolio.noInstrumentMatch": {
    pt: "Nenhum instrumento encontrado",
    en: "No instrument found",
  },
  "portfolio.noLocalInstrumentMatch": {
    pt: "Nenhum instrumento carregado corresponde à busca. A lista local pode estar incompleta.",
    en: "No loaded instrument matches this search. The local list may be incomplete.",
  },
  "portfolio.noLocalInstrumentMatchExternal": {
    pt: "Nenhum instrumento carregado corresponde à busca. A lista local pode estar incompleta. " +
      "Use a busca externa abaixo.",
    en: "No loaded instrument matches this search. The local list may be incomplete. " +
      "Use the external search below.",
  },
  "portfolio.changeInstrument": {
    pt: "Trocar",
    en: "Change",
  },
  "portfolio.requestByIdentifier": {
    pt: "Solicitar cadastro por ISIN/FIGI",
    en: "Request instrument by ISIN/FIGI",
  },
  "portfolio.instrumentRequested": {
    pt: "Solicitação enviada — o instrumento aparecerá após a resolução",
    en: "Request filed — the instrument will appear once resolved",
  },
  "portfolio.instrumentRequestFailed": {
    pt: "Não foi possível solicitar o instrumento",
    en: "Could not request the instrument",
  },
  "portfolio.side": {
    pt: "Tipo de operação",
    en: "Side",
  },
  "portfolio.side.buy": {
    pt: "Compra",
    en: "Buy",
  },
  "portfolio.side.sell": {
    pt: "Venda",
    en: "Sell",
  },
  "portfolio.quantity": {
    pt: "Quantidade",
    en: "Quantity",
  },
  "portfolio.quantityInvalid": {
    pt: "Informe uma quantidade válida, como {example}",
    en: "Enter a valid quantity, like {example}",
  },
  "portfolio.price": {
    pt: "Preço unitário",
    en: "Unit price",
  },
  "portfolio.priceInvalid": {
    pt: "Informe um preço válido, como {example}",
    en: "Enter a valid price, like {example}",
  },
  "portfolio.currency": {
    pt: "Moeda",
    en: "Currency",
  },
  "portfolio.currencyFromInstrument": {
    pt: "Definida pelo instrumento",
    en: "Set by the instrument",
  },
  "portfolio.referenceTime": {
    pt: "Data da operação",
    en: "Trade date",
  },
  "portfolio.referenceTimeRequired": {
    pt: "Informe a data da operação",
    en: "Trade date is required",
  },
  "portfolio.searchExternal": {
    pt: "Buscar instrumento externo",
    en: "Search external instrument",
  },
  "portfolio.searching": {
    pt: "Buscando...",
    en: "Searching...",
  },
  "portfolio.externalResults": {
    pt: "Resultados externos",
    en: "External results",
  },
  "portfolio.assetClass": {
    pt: "Classe de ativo",
    en: "Asset class",
  },
  "portfolio.comingSoon": {
    pt: "(em breve)",
    en: "(coming soon)",
  },
  "portfolio.countryFilter": {
    pt: "País",
    en: "Country",
  },
  "portfolio.allCountries": {
    pt: "Todos os países",
    en: "All countries",
  },
  "portfolio.previewResultCount": {
    pt: "{count} resultados",
    en: "{count} results",
  },
  "portfolio.previewResultCountOne": {
    pt: "1 resultado",
    en: "1 result",
  },
  "portfolio.localResultCount": {
    pt: "Mostrando {shown} de {total}",
    en: "Showing {shown} of {total}",
  },
  "portfolio.noExternalMatch": {
    pt: "Nenhum instrumento externo encontrado",
    en: "No external instrument found",
  },
  "portfolio.instrumentResolving": {
    pt: "Registrando instrumento...",
    en: "Registering instrument...",
  },
  "portfolio.instrumentStillResolving": {
    pt: "O instrumento ainda está sendo registrado. Tente novamente em instantes.",
    en: "The instrument is still being registered. Try again shortly.",
  },
  "portfolio.record": {
    pt: "Registrar",
    en: "Record",
  },
  "portfolio.recording": {
    pt: "Registrando...",
    en: "Recording...",
  },
  "portfolio.tradeRecorded": {
    pt: "Operação registrada",
    en: "Trade recorded",
  },
  "portfolio.col.actions": {
    pt: "Ações",
    en: "Actions",
  },
  "portfolio.snapshotEdit": {
    pt: "Editar posição",
    en: "Edit position",
  },
  "portfolio.snapshotTitle": {
    pt: "Declarar posição",
    en: "Declare position",
  },
  "portfolio.snapshotDescription": {
    pt: "Declare a quantidade verdadeira desta posição no momento de referência. O sistema registra uma operação de ajuste para chegar à quantidade declarada.",
    en: "Declare this position's true quantity at the reference time. The system records an adjustment trade to reach the declared quantity.",
  },
  "portfolio.snapshotCurrentQuantity": {
    pt: "Quantidade atual",
    en: "Current quantity",
  },
  "portfolio.snapshotQuantity": {
    pt: "Quantidade declarada",
    en: "Declared quantity",
  },
  "portfolio.snapshotPrice": {
    pt: "Preço unitário (opcional)",
    en: "Unit price (optional)",
  },
  "portfolio.snapshotPriceHint": {
    pt: "Sem preço, o ajuste é registrado sem custo de aquisição e o resultado do período fica incompleto.",
    en: "Without a price, the adjustment books with no cost basis and period profit figures stay incomplete.",
  },
  "portfolio.snapshotDeclare": {
    pt: "Declarar",
    en: "Declare",
  },
  "portfolio.snapshotDeclaring": {
    pt: "Declarando...",
    en: "Declaring...",
  },
  "portfolio.snapshotDeclared": {
    pt: "Posição declarada",
    en: "Position declared",
  },
  "portfolio.snapshotStillProcessing": {
    pt: "A declaração ainda está em processamento — os valores atualizam em instantes",
    en: "The declaration is still processing — values update shortly",
  },
  "portfolio.snapshotFailed": {
    pt: "O ajuste da declaração falhou — verifique a posição e tente novamente",
    en: "The declaration's adjustment failed — check the position and try again",
  },
  "portfolio.snapshotReferenceTime": {
    pt: "Data de referência",
    en: "Reference date",
  },
  "portfolio.snapshotReferenceTimeRequired": {
    pt: "Informe a data de referência",
    en: "Reference date is required",
  },
  "portfolio.back": {
    pt: "Voltar aos portfólios",
    en: "Back to portfolios",
  },
  "portfolio.instrumentNoAccountScope": {
    pt: "Selecione uma conta para buscar e registrar novos instrumentos",
    en: "Select an account to search for and register new instruments",
  },
  "portfolio.investorsUnavailable": {
    pt: "Não foi possível carregar os investidores. Tente novamente.",
    en: "The investor list could not be loaded. Try again.",
  },
  "portfolio.positionsUnavailable": {
    pt: "Não foi possível carregar as posições desta carteira",
    en: "Could not load this portfolio's positions",
  },
  "portfolio.operationsUnavailable": {
    pt: "Não foi possível carregar as operações desta carteira",
    en: "Could not load this portfolio's operations",
  },
  "portfolio.summaryUnavailable": {
    pt: "Não foi possível carregar o resumo desta carteira; patrimônio, alocação e participações não estão disponíveis",
    en: "Could not load this portfolio's summary; NAV, allocation and weights are unavailable",
  },
  "portfolio.summaryRefreshFailed": {
    pt: "Não foi possível atualizar o resumo; os valores exibidos são os últimos disponíveis",
    en: "Could not refresh the summary; the figures shown are the last available",
  },
  "portfolio.signInAgain": {
    pt: "Entrar novamente",
    en: "Sign in again",
  },
  "portfolio.retry": {
    pt: "Tentar novamente",
    en: "Retry",
  },
  "portfolio.truncatedPortfolios": {
    pt: "Mostrando apenas os primeiros resultados.",
    en: "Showing the first results only.",
  },
  "portfolio.truncatedPositions": {
    pt: "Apenas as {limit} primeiras posições são listadas. A ordenação e os percentuais cobrem somente essas linhas.",
    en: "Only the first {limit} positions are listed. The order and the percentages cover those rows only.",
  },
  "portfolio.weightsPerCurrency": {
    pt: "Sem a taxa de câmbio, cada percentual é a parcela do total da própria moeda da posição. Percentuais de moedas diferentes não podem ser comparados entre si.",
    en: "With no exchange rate, each percentage is a share of its own position's currency total. Percentages of different currencies cannot be compared with each other.",
  },
  "portfolio.weightsZeroTotal": {
    pt: "O total deste portfólio é zero, portanto nenhuma posição tem uma parcela que possa ser medida. Não há percentuais para ordenar. As demais colunas continuam ordenáveis.",
    en: "This portfolio's total is zero, so no position has a share that can be measured. There are no percentages to rank. The other columns still sort.",
  },
  "portfolio.truncatedBlotter": {
    pt: "Mostrando apenas os primeiros resultados desta ordenação. Trocar a ordem muda quais operações aparecem.",
    en: "Showing the first results for this ordering only. Changing the order changes which operations appear.",
  },
  "portfolio.tradesEmptyFiltered": {
    pt: "Nenhuma operação corresponde a este filtro. Marque \"Mostrar canceladas\" para ver as canceladas.",
    en: "No operations match this view. Tick \"Show canceled\" to include canceled ones.",
  },
  "portfolio.notFound": {
    pt: "Portfólio não encontrado",
    en: "Portfolio not found",
  },
  "portfolio.allocationFxUnavailable": {
    pt: "A alocação por classe aparece quando a taxa de câmbio estiver disponível.",
    en: "Class allocation appears once the exchange rate is available.",
  },
  "portfolio.navCurrency": {
    pt: "Moeda de exibição",
    en: "Display currency",
  },
  "portfolio.navCurrencyFailed": {
    pt: "Não foi possível mostrar os valores em {attempted}. Nada mudou.",
    en: "The figures could not be shown in {attempted}. Nothing changed.",
  },
  "portfolio.navTotalUnrepresentable": {
    pt: "O total é grande demais para ser mostrado com exatidão, então os valores aparecem por moeda.",
    en: "The total is too large to show exactly, so the figures are shown per currency.",
  },
  "portfolio.navFxUnavailable": {
    pt: "Sem taxa de câmbio para somar as moedas. Os valores aparecem por moeda.",
    en: "No exchange rate to add the currencies up. The figures are shown per currency.",
  },
  "portfolio.navPricesPending": {
    pt: "Nenhuma posição tem preço ainda, então não há total para mostrar.",
    en: "No position has a price yet, so there is no total to show.",
  },
  "portfolio.navHistory.title": {
    pt: "Histórico do valor do portfólio (NAV)",
    en: "Portfolio value (NAV) history",
  },
  "portfolio.navHistory.period": {
    pt: "Período",
    en: "Period",
  },
  "portfolio.navHistory.empty": {
    pt: "Ainda não há histórico. O histórico começa no dia da primeira operação ou do primeiro preço, o que vier depois.",
    en: "No history yet. History starts on the day of the first trade or the first price, whichever comes later.",
  },
  "portfolio.navHistory.emptyPeriod": {
    pt: "Nenhum dia avaliado neste período. Escolha um período maior.",
    en: "No valued day in this period. Pick a longer period.",
  },
  "portfolio.navHistory.noTotal": {
    pt: "Nenhum dia deste período tem um total consolidado para desenhar.",
    en: "No day in this period has a consolidated total to draw.",
  },
  "portfolio.navHistory.loadFailed": {
    pt: "Não foi possível carregar o histórico do valor do portfólio",
    en: "Could not load the portfolio value history",
  },
  "portfolio.navHistory.tooLong": {
    pt: "O histórico deste portfólio é mais longo do que o Console consegue percorrer em uma leitura.",
    en: "This portfolio's history is longer than the Console can page through in one read.",
  },
  "portfolio.navHistory.singlePoint": {
    pt: "Só há um dia avaliado até agora — ainda não é suficiente para desenhar uma linha de tendência.",
    en: "Only one valued day so far — not enough to draw a trend line yet.",
  },
  "portfolio.navHistory.refreshFailed": {
    pt: "Não foi possível carregar o histórico de {period} em {currency}. A série exibida é a última carregada: {servedPeriod} em {servedCurrency}.",
    en: "Could not load the {period} history in {currency}. The series shown is the last one loaded: {servedPeriod} in {servedCurrency}.",
  },
  "portfolio.navHistory.staleNote": {
    pt: "Não foi possível atualizar o histórico; a série exibida é a última carregada.",
    en: "Could not refresh the history; the series shown is the last one loaded.",
  },
  "portfolio.navHistory.lastValue": {
    pt: "Último valor",
    en: "Latest value",
  },
  "portfolio.navHistory.range": {
    pt: "Período exibido",
    en: "Shown range",
  },
  "portfolio.navHistory.points": {
    pt: "Dias avaliados",
    en: "Valued days",
  },
  "portfolio.navHistory.now": {
    pt: "Agora",
    en: "Now",
  },
  "portfolio.navHistory.nowNote": {
    pt: "O trecho tracejado termina no valor atual.",
    en: "The dashed segment ends at the current value.",
  },
  "portfolio.navHistory.partialNote": {
    pt: "Um ponto vazado marca um dia incompleto: alguma posição sem preço, ou sem taxa de câmbio para somar as moedas.",
    en: "A hollow point marks an incomplete day: a position had no price, or there was no exchange rate to add the currencies up.",
  },
  "portfolio.navHistory.partialUnpriced": {
    pt: "Incompleto: {count} posição(ões) sem preço neste dia.",
    en: "Incomplete: {count} position(s) had no price on this day.",
  },
  "portfolio.navHistory.partialRateMissing": {
    pt: "Sem total: não havia taxa de câmbio para somar as moedas neste dia.",
    en: "No total: there was no exchange rate to add the currencies up on this day.",
  },
  "portfolio.navHistory.partialNothingPriced": {
    pt: "Sem total: nenhuma posição tinha preço neste dia.",
    en: "No total: no position had a price on this day.",
  },
  "status.created": {
    pt: "Criado",
    en: "Created",
  },
  "status.active": {
    pt: "Ativo",
    en: "Active",
  },
  "status.blocked": {
    pt: "Bloqueado",
    en: "Blocked",
  },
  "status.frozen": {
    pt: "Congelado",
    en: "Frozen",
  },
  "status.pending": {
    pt: "Pendente",
    en: "Pending",
  },
  "status.processing": {
    pt: "Processando",
    en: "Processing",
  },
  "status.failed": {
    pt: "Falhou",
    en: "Failed",
  },
  "status.rejected": {
    pt: "Rejeitado",
    en: "Rejected",
  },
  "status.canceled": {
    pt: "Cancelado",
    en: "Canceled",
  },
  "status.expired": {
    pt: "Expirado",
    en: "Expired",
  },
  "status.success": {
    pt: "Concluído",
    en: "Success",
  },
  "status.closed": {
    pt: "Encerrado",
    en: "Closed",
  },
  "sidebar.orders": {
    pt: "Ordens",
    en: "Orders",
  },
  "sidebar.logout": {
    pt: "Sair",
    en: "Logout",
  },
  "sidebar.currentAccount": {
    pt: "Conta Atual",
    en: "Current Account",
  },
  "sidebar.accountName": {
    pt: "Nome",
    en: "Name",
  },
  "sidebar.accountId": {
    pt: "ID",
    en: "ID",
  },
  "sidebar.currentAccountHolder": {
    pt: "Organização Atual",
    en: "Current Organization",
  },
  "sidebar.switchAccount": {
    pt: "Trocar de conta",
    en: "Switch account",
  },
  "sidebar.accounts": {
    pt: "Contas",
    en: "Accounts",
  },
  "sidebar.loadingAccounts": {
    pt: "Carregando contas...",
    en: "Loading accounts...",
  },
  "sidebar.accountsError": {
    pt: "Não foi possível carregar as contas",
    en: "Could not load accounts",
  },
  "sidebar.holdersError": {
    pt: "Não foi possível carregar as organizações",
    en: "Could not load organizations",
  },
  "sidebar.menuError": {
    pt: "Não foi possível carregar o menu completo",
    en: "Could not load the full menu",
  },
  "sidebar.retry": {
    pt: "Tentar novamente",
    en: "Try again",
  },
  "sidebar.noOtherAccounts": {
    pt: "Nenhuma outra conta disponível",
    en: "No other accounts available",
  },
  "sidebar.createAccount": {
    pt: "Criar conta",
    en: "Create account",
  },
  "sidebar.changeAccountHolder": {
    pt: "Trocar de organização",
    en: "Change organization",
  },

  // Console/Dashboard
  "console.loading": {
    pt: "Carregando console...",
    en: "Loading console...",
  },
  "console.error": {
    pt: "Erro:",
    en: "Error:",
  },
  "console.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "console.dashboard": {
    pt: "Dashboard",
    en: "Dashboard",
  },
  "console.welcome": {
    pt: "Bem-vindo(a), {name}",
    en: "Welcome, {name}",
  },
  "console.subtitle": {
    pt: "Acompanhe a movimentação de seus clientes em tempo real",
    en: "Track your clients' activity in real time",
  },
  "console.transactionsToday": {
    pt: "Transações Hoje",
    en: "Transactions Today",
  },
  "console.volumeToday": {
    pt: "Volume Hoje",
    en: "Volume Today",
  },
  "console.status": {
    pt: "Status",
    en: "Status",
  },
  "console.session": {
    pt: "Sessão",
    en: "Session",
  },
  "console.noData": {
    pt: "Sem dados",
    en: "No data",
  },
  "console.active": {
    pt: "Ativo",
    en: "Active",
  },
  "console.systemOperational": {
    pt: "Sistema operacional",
    en: "System operational",
  },
  "console.authenticated": {
    pt: "Autenticado",
    en: "Authenticated",
  },
  "console.scopeInfo": {
    pt: "Informações",
    en: "Information",
  },
  "console.accountHolder": {
    pt: "Organização",
    en: "Organization",
  },
  "console.account": {
    pt: "Conta",
    en: "Account",
  },
  "console.taxId": {
    pt: "CNPJ",
    en: "Tax ID",
  },
  "console.id": {
    pt: "ID",
    en: "ID",
  },
  "console.authentication": {
    pt: "Autenticação",
    en: "Authentication",
  },
  "console.authSuccess": {
    pt: "Autenticado com sucesso",
    en: "Successfully authenticated",
  },
  "console.method": {
    pt: "Método",
    en: "Method",
  },
  "console.sessionId": {
    pt: "Session ID",
    en: "Session ID",
  },

  // Access Management
  "access.title": {
    pt: "Gerenciamento de Acessos",
    en: "Access Management",
  },
  "access.subtitle": {
    pt: "Gerencie permissões e acessos do sistema",
    en: "Manage system permissions and access",
  },
  "access.developing": {
    pt: "Funcionalidade de gerenciamento de acessos em desenvolvimento",
    en: "Access management functionality under development",
  },
  "access.comingSoon": {
    pt: "Esta página será implementada em breve",
    en: "This page will be implemented soon",
  },
  "access.loading": {
    pt: "Carregando usuários...",
    en: "Loading users...",
  },
  "access.error": {
    pt: "Erro",
    en: "Error",
  },
  "access.searchPlaceholder": {
    pt: "Buscar usuários...",
    en: "Search users...",
  },
  "access.accountUsers": {
    pt: "Acesso da conta",
    en: "Account access",
  },
  "access.holderUsers": {
    pt: "Acesso da organização",
    en: "Organization access",
  },
  "access.rules": {
    pt: "Função",
    en: "Role",
  },
  "access.status": {
    pt: "Situação do usuário",
    en: "User status",
  },
  "access.active": {
    pt: "Ativo",
    en: "Active",
  },
  "access.typeExecutive": {
    pt: "Executivo",
    en: "Executive",
  },
  "access.typeSysadmin": {
    pt: "Administrador do Sistema",
    en: "Sysadmin",
  },
  "access.typeService": {
    pt: "Serviço",
    en: "Service",
  },
  "access.typeSession": {
    pt: "Sessão",
    en: "Session",
  },
  "access.typeMailbox": {
    pt: "Caixa de Entrada",
    en: "Mailbox",
  },
  "access.typeOpen": {
    pt: "Aberto",
    en: "Open",
  },
  "access.typeUnknown": {
    pt: "Desconhecido",
    en: "Unknown",
  },
  "access.statusCreated": {
    pt: "Criado",
    en: "Created",
  },
  "access.statusBlocked": {
    pt: "Bloqueado",
    en: "Blocked",
  },
  "access.statusFrozen": {
    pt: "Congelado",
    en: "Frozen",
  },
  "access.statusClosed": {
    pt: "Encerrado",
    en: "Closed",
  },
  "access.loadMore": {
    pt: "Carregar mais",
    en: "Load more",
  },
  "access.loadingMore": {
    pt: "Carregando mais acessos...",
    en: "Loading more access...",
  },
  "access.truncated": {
    pt: "Esta lista é longa demais para ser carregada por completo. Parte dos acessos não aparece aqui.",
    en: "This list is too long to load completely. Some access is not shown here.",
  },
  "access.systemManagedRole": {
    pt: "Esta função é atribuída pelo sistema e não pode ser alterada aqui.",
    en: "This role is granted by the system and cannot be changed here.",
  },
  "access.inviteScopeNote": {
    pt: "O convite dá acesso à conta:",
    en: "The invitation grants access to the account:",
  },
  "access.notFullyLoaded": {
    pt: "A lista não foi carregada por completo, então não é possível dizer se há mais acessos.",
    en: "The list was not fully loaded, so we cannot say whether there is more access.",
  },
  "access.stillLoading": {
    pt: "Procurando no restante dos acessos...",
    en: "Looking through the remaining access...",
  },
  "access.loadMoreFailed": {
    pt: "Não foi possível carregar mais acessos.",
    en: "Could not load more access.",
  },
  "access.loadFailed": {
    pt: "Não foi possível carregar os acessos.",
    en: "Could not load the access list.",
  },
  "access.accountRequired": {
    pt: "Selecione uma conta para ver os acessos.",
    en: "Select an account to see its access.",
  },
  "access.noAccountScope": {
    pt: "Nenhuma conta no escopo atual.",
    en: "No account in the current scope.",
  },
  "access.noHolderScope": {
    pt: "Nenhuma organização no escopo atual.",
    en: "No organization in the current scope.",
  },
  "access.noScopeContext": {
    pt: "Nenhum escopo disponível. Selecione uma conta de novo.",
    en: "No scope available. Select an account again.",
  },
  "access.signInAgain": {
    pt: "Entrar de novo",
    en: "Sign in again",
  },
  "access.systemManagedAccess": {
    pt: "Este acesso inclui uma função do sistema, por isso não pode ser alterado aqui.",
    en: "This access includes a system role, so it cannot be changed here.",
  },
  "access.noResultsSoFar": {
    pt: "Nenhum acesso corresponde à sua busca na parte já carregada da lista.",
    en: "No access matches your search in the part of the list loaded so far.",
  },
  "access.moreMayMatch": {
    pt: "Parte da lista ainda não foi carregada, então pode haver mais correspondências.",
    en: "Part of the list is not loaded yet, so there may be more matches.",
  },
  "access.searchTheRest": {
    pt: "Buscar no resto da lista",
    en: "Search the rest of the list",
  },
  "access.noResults": {
    pt: "Nenhum acesso corresponde à sua busca",
    en: "No access matches your search",
  },
  "access.clearSearch": {
    pt: "Limpar busca",
    en: "Clear search",
  },
  "access.continueLoading": {
    pt: "Continuar carregando",
    en: "Keep loading",
  },
  "access.you": {
    pt: "Você",
    en: "You",
  },
  "access.revokeSelfWarning": {
    pt: "Este é o seu próprio acesso. Ao remover, você perde o acesso a este escopo e não poderá devolvê-lo por esta tela.",
    en: "This is your own access. Removing it costs you access to this scope, and this screen cannot give it back.",
  },
  "access.revokeMaybeSelfWarning": {
    pt: "Não foi possível confirmar de quem é este acesso. Se ele for o seu, você perde o acesso a este escopo e não poderá devolvê-lo por esta tela.",
    en: "We could not confirm whose access this is. If it is your own, removing it costs you access to this scope, and this screen cannot give it back.",
  },
  "access.revokeSelfAcknowledge": {
    pt: "Eu entendo e quero remover o meu acesso",
    en: "I understand and want to remove my access",
  },
  "access.removeRole": {
    pt: "Remover função",
    en: "Remove role",
  },
  "access.noSession": {
    pt: "Sessão não encontrada. Faça login novamente.",
    en: "No session found. Please sign in again.",
  },
  "access.noScope": {
    pt: "Nenhuma conta selecionada. Selecione uma conta.",
    en: "No account selected. Please select an account.",
  },
  "access.incompleteScope": {
    pt: "A seleção de conta está incompleta. Selecione uma conta.",
    en: "The account selection is incomplete. Please select an account.",
  },
  "access.unknownUser": {
    pt: "Usuário desconhecido",
    en: "Unknown user",
  },
  "access.userType": {
    pt: "Tipo",
    en: "Type",
  },
  "access.typePerson": {
    pt: "Pessoa",
    en: "Person",
  },
  "access.typeIntegration": {
    pt: "Integração",
    en: "Integration",
  },
  "access.statusUnknown": {
    pt: "Desconhecido",
    en: "Unknown",
  },
  "access.retry": {
    pt: "Tentar de novo",
    en: "Try again",
  },
  "access.atLeastOneProfile": {
    pt: "Selecione no mínimo uma função. Para remover o acesso, use a ação de revogar.",
    en: "Select at least one role. To remove the access, use the revoke action.",
  },
  "access.updateRolesFailed": {
    pt: "Não foi possível atualizar as funções.",
    en: "Could not update the roles.",
  },
  "access.revokeTitle": {
    pt: "Revogar acesso",
    en: "Revoke access",
  },
  "access.revokeDesc": {
    pt: "Isto remove o acesso deste usuário a este escopo:",
    en: "This removes this user's access to this scope:",
  },
  "access.revokeConfirm": {
    pt: "Revogar",
    en: "Revoke",
  },
  "access.revoking": {
    pt: "Revogando...",
    en: "Revoking...",
  },
  "access.accessRevoked": {
    pt: "Acesso revogado",
    en: "Access revoked",
  },
  "access.accessRevokedDesc": {
    pt: "O acesso do usuário a este escopo foi removido.",
    en: "The user's access to this scope was removed.",
  },
  "access.revokeFailed": {
    pt: "Não foi possível revogar o acesso.",
    en: "Could not revoke the access.",
  },
  "access.newInvite": {
    pt: "Nova Invitação",
    en: "New Invitation",
  },
  "access.emailPlaceholder": {
    pt: "Endereço de e-mail",
    en: "Email address",
  },
  "access.selectRole": {
    pt: "Selecionar função",
    en: "Select role",
  },
  "access.loadingRoles": {
    pt: "Carregando funções...",
    en: "Loading roles...",
  },
  "access.sendInvite": {
    pt: "Enviar Convite",
    en: "Send Invite",
  },
  "access.sending": {
    pt: "Enviando...",
    en: "Sending...",
  },
  "access.inviteSent": {
    pt: "Convite Enviado",
    en: "Invitation Sent",
  },
  "access.ok": {
    pt: "OK",
    en: "OK",
  },
  "access.requiredFields": {
    pt: "Campos obrigatórios",
    en: "Required fields",
  },
  "access.fillAllFields": {
    pt: "Por favor, preencha todos os campos",
    en: "Please fill in all fields",
  },
  "access.invalidEmail": {
    pt: "E-mail inválido",
    en: "Invalid email",
  },
  "access.invalidEmailDesc": {
    pt: "Por favor, insira um endereço de e-mail válido",
    en: "Please enter a valid email address",
  },
  "access.editUserRoles": {
    pt: "Editar Funções do Usuário",
    en: "Edit User Roles",
  },
  "access.editUserRolesDesc": {
    pt: "Gerenciar funções para",
    en: "Manage roles for",
  },
  "access.addRole": {
    pt: "Adicionar Função",
    en: "Add Role",
  },
  "access.userRolesUpdated": {
    pt: "Funções atualizadas",
    en: "Roles updated",
  },
  "access.userRolesUpdatedDesc": {
    pt: "As funções do usuário foram atualizadas com sucesso.",
    en: "User roles have been successfully updated.",
  },
  "access.cancel": {
    pt: "Cancelar",
    en: "Cancel",
  },
  "access.confirm": {
    pt: "Confirmar",
    en: "Confirm",
  },
  "access.updating": {
    pt: "Atualizando...",
    en: "Updating...",
  },
  "access.tabPersons": {
    pt: "Pessoas",
    en: "Persons",
  },
  "access.tabIntegrations": {
    pt: "Integrações",
    en: "Integrations",
  },
  "access.tabHistory": {
    pt: "Histórico de acessos",
    en: "Access History",
  },
  "access.historyFilters": {
    pt: "Filtros",
    en: "Filters",
  },
  "access.accessStatus": {
    pt: "Situação do acesso",
    en: "Access status",
  },
  "access.anyStatus": {
    pt: "Qualquer situação",
    en: "Any status",
  },
  "access.accessCanceled": {
    pt: "Cancelado",
    en: "Canceled",
  },
  "access.grantedAfter": {
    pt: "Concedido a partir de",
    en: "Granted from",
  },
  "access.grantedBefore": {
    pt: "Concedido até",
    en: "Granted until",
  },
  "access.clearFilters": {
    pt: "Limpar filtros",
    en: "Clear filters",
  },
  "access.noPersons": {
    pt: "Nenhuma pessoa tem acesso a esta conta",
    en: "No person has access to this account",
  },
  "access.noHolderPersons": {
    pt: "Nenhuma pessoa tem acesso a esta organização",
    en: "No person has access to this organization",
  },
  "access.noIntegrations": {
    pt: "Nenhuma integração tem acesso a esta conta",
    en: "No integration has access to this account",
  },
  "access.noHolderIntegrations": {
    pt: "Nenhuma integração tem acesso a esta organização",
    en: "No integration has access to this organization",
  },
  "access.noHistory": {
    pt: "Nenhum acesso corresponde a estes filtros",
    en: "No access matches these filters",
  },
  "access.granted": {
    pt: "Concedido em",
    en: "Granted",
  },
  "access.revokedAccess": {
    pt: "Acesso cancelado",
    en: "Access canceled",
  },
  "access.personsDesc": {
    pt: "Pessoas com acesso ativo a esta conta",
    en: "People with active access to this account",
  },
  "access.holderPersonsDesc": {
    pt: "Pessoas com acesso ativo a todas as contas da organização",
    en: "People with active access to every account in the organization",
  },
  "access.integrationsDesc": {
    pt: "Integrações com acesso ativo a esta conta",
    en: "Integrations with active access to this account",
  },
  "access.holderIntegrationsDesc": {
    pt: "Integrações com acesso ativo a todas as contas da organização",
    en: "Integrations with active access to every account in the organization",
  },
  "access.historyDesc": {
    pt: "Todos os acessos já concedidos a esta conta, ativos e cancelados",
    en: "Every access ever granted to this account, active and canceled",
  },
  "access.holderHistoryDesc": {
    pt: "Todos os acessos já concedidos à organização, ativos e cancelados",
    en: "Every access ever granted to the organization, active and canceled",
  },
  "access.noAnyAccess": {
    pt: "Nenhum acesso foi concedido a esta conta",
    en: "No access has ever been granted to this account",
  },
  "access.noHolderAnyAccess": {
    pt: "Nenhum acesso foi concedido a esta organização",
    en: "No access has ever been granted to this organization",
  },
  "access.revokedActionsNote": {
    pt: "Este acesso já foi cancelado e não pode ser alterado",
    en: "This access is already canceled and cannot be changed",
  },

  // User Invite
  "userInvite.title": {
    pt: "Convite",
    en: "Invitation",
  },
  "userInvite.subtitle": {
    pt: "Você foi convidado para participar",
    en: "You have been invited to join",
  },
  "userInvite.initializing": {
    pt: "Carregando convite...",
    en: "Loading invitation...",
  },
  "userInvite.invalidLink": {
    pt: "Link de convite inválido",
    en: "Invalid invitation link",
  },
  "userInvite.missingParams": {
    pt: "Parâmetros de sessão ausentes",
    en: "Missing session parameters",
  },
  "userInvite.userInfo": {
    pt: "Informações do Usuário",
    en: "User Information",
  },
  "userInvite.accept": {
    pt: "Aceitar Convite",
    en: "Accept Invitation",
  },
  "userInvite.deny": {
    pt: "Recusar Convite",
    en: "Deny Invitation",
  },
  "userInvite.processing": {
    pt: "Processando...",
    en: "Processing...",
  },
  "userInvite.inviteAccepted": {
    pt: "Convite Aceito",
    en: "Invitation Accepted",
  },
  "userInvite.inviteAcceptedDesc": {
    pt: "Você aceitou o convite com sucesso.",
    en: "You have successfully accepted the invitation.",
  },
  "userInvite.inviteDenied": {
    pt: "Convite Recusado",
    en: "Invitation Denied",
  },
  "userInvite.inviteDeniedDesc": {
    pt: "Você recusou o convite.",
    en: "You have declined the invitation.",
  },
  "userInvite.createUser": {
    pt: "Criar Usuário",
    en: "Create User",
  },
  "userInvite.createUserDesc": {
    pt: "Complete seu cadastro para aceitar o convite",
    en: "Complete your registration to accept the invitation",
  },
  "userInvite.name": {
    pt: "Nome Completo",
    en: "Full Name",
  },
  "userInvite.namePlaceholder": {
    pt: "Digite seu nome completo",
    en: "Enter your full name",
  },
  "userInvite.nameRequired": {
    pt: "Nome é obrigatório",
    en: "Name is required",
  },
  "userInvite.taxId": {
    pt: "CPF",
    en: "CPF (Tax ID)",
  },
  "userInvite.taxIdPlaceholder": {
    pt: "000.000.000-00",
    en: "000.000.000-00",
  },
  "userInvite.taxIdHint": {
    pt: "Digite seu CPF brasileiro (11 dígitos)",
    en: "Enter your Brazilian CPF (11 digits)",
  },
  "userInvite.taxIdRequired": {
    pt: "CPF é obrigatório",
    en: "Tax ID (CPF) is required",
  },
  "userInvite.invalidTaxId": {
    pt: "CPF inválido. Por favor, digite 11 dígitos",
    en: "Invalid CPF. Please enter 11 digits",
  },
  "userInvite.phone": {
    pt: "Telefone",
    en: "Phone Number",
  },
  "userInvite.phonePlaceholder": {
    pt: "+5511999999999",
    en: "+5511999999999",
  },
  "userInvite.phoneRequired": {
    pt: "Telefone é obrigatório",
    en: "Phone number is required",
  },
  "userInvite.creating": {
    pt: "Criando...",
    en: "Creating...",
  },
  "userInvite.userCreated": {
    pt: "Usuário Criado",
    en: "User Created",
  },
  "userInvite.userCreatedDesc": {
    pt: "Seu usuário foi criado com sucesso.",
    en: "Your user has been created successfully.",
  },
  "userInvite.successTitle": {
    pt: "Sucesso",
    en: "Success",
  },
  "userInvite.successMessage": {
    pt: "Operação concluída com sucesso",
    en: "Operation completed successfully",
  },
  "userInvite.redirecting": {
    pt: "Redirecionando para a página de login...",
    en: "Redirecting to login page...",
  },
  "userInvite.backToLogin": {
    pt: "Voltar para Login",
    en: "Back to Login",
  },
  "userInvite.error": {
    pt: "Erro",
    en: "Error",
  },
  "userInvite.emailNotFound": {
    pt: "E-mail não encontrado no convite",
    en: "Email not found in invitation",
  },
  "userInvite.password": {
    pt: "Senha",
    en: "Password",
  },
  "userInvite.confirmPassword": {
    pt: "Confirmar Senha",
    en: "Confirm Password",
  },
  "userInvite.passwordRequired": {
    pt: "Senha é obrigatória",
    en: "Password is required",
  },
  "userInvite.passwordMismatch": {
    pt: "As senhas não coincidem",
    en: "Passwords do not match",
  },
  "userInvite.passwordMinLength": {
    pt: "A senha deve ter pelo menos 8 caracteres",
    en: "Password must be at least 8 characters",
  },
  "userInvite.invalidInvite": {
    pt: "Convite inválido",
    en: "Invalid invitation",
  },
  "userInvite.invitedTo": {
    pt: "Você está sendo convidado para:",
    en: "You are being invited to:",
  },
  "userInvite.account": {
    pt: "Conta",
    en: "Account",
  },
  "userInvite.accountHolder": {
    pt: "Organização",
    en: "Organization",
  },
  "userInvite.scopeId": {
    pt: "ID do Escopo",
    en: "Scope ID",
  },
  "userInvite.verifyPhoneTitle": {
    pt: "Verifique seu telefone",
    en: "Verify your phone",
  },
  "userInvite.verifyPhoneDesc": {
    pt: "Enviaremos um código de 6 dígitos por SMS para confirmar seu número.",
    en: "We'll send a 6-digit code by SMS to confirm your number.",
  },
  "userInvite.enterTokenTitle": {
    pt: "Digite o código que enviamos",
    en: "Enter the code we sent you",
  },
  "userInvite.enterTokenDesc": {
    pt: "Enviamos um código de 6 dígitos para {phone}. Digite-o abaixo.",
    en: "We sent a 6-digit code to {phone}. Enter it below.",
  },
  "userInvite.phoneNumber": {
    pt: "Número de telefone",
    en: "Phone number",
  },
  "userInvite.phoneVerified": {
    pt: "Telefone verificado:",
    en: "Verified phone:",
  },
  "userInvite.sendCode": {
    pt: "Enviar código",
    en: "Send code",
  },
  "userInvite.sending": {
    pt: "Enviando...",
    en: "Sending...",
  },
  "userInvite.token": {
    pt: "Código de 6 dígitos",
    en: "6-digit code",
  },
  "userInvite.tokenLength": {
    pt: "Digite o código de 6 dígitos",
    en: "Enter the 6-digit code",
  },
  "userInvite.verify": {
    pt: "Verificar",
    en: "Verify",
  },
  "userInvite.verifying": {
    pt: "Verificando...",
    en: "Verifying...",
  },
  "userInvite.resend": {
    pt: "Reenviar código",
    en: "Resend code",
  },
  "userInvite.resendCooldown": {
    pt: "Reenvio disponível em {seconds}s",
    en: "Resend available in {seconds}s",
  },


  // Integrations
  "integrations.title": {
    pt: "Integrações",
    en: "Integrations",
  },
  "integrations.subtitle": {
    pt: "Gerencie suas integrações e crie novas integrações",
    en: "Manage your integrations and create new integrations",
  },
  "integrations.loading": {
    pt: "Carregando integrações...",
    en: "Loading integrations...",
  },
  "integrations.error": {
    pt: "Erro",
    en: "Error",
  },
  "integrations.accountIntegrations": {
    pt: "Integrações da conta",
    en: "Account Integrations",
  },
  "integrations.holderIntegrations": {
    pt: "Integrações da organização",
    en: "Organization Integrations",
  },
  "integrations.tabManagement": {
    pt: "Gerenciamento de integrações",
    en: "Integration Management",
  },
  "integrations.tabAccess": {
    pt: "Acesso de integrações",
    en: "Integration Access",
  },
  "integrations.accountIntegrationsDesc": {
    pt: "Integrações criadas na conta atual",
    en: "Integrations created in the current account",
  },
  "integrations.holderIntegrationsDesc": {
    pt: "Integrações criadas na organização",
    en: "Integrations created in the organization",
  },
  "integrations.accountAccessDesc": {
    pt: "Integrações com acesso à conta atual",
    en: "Integrations with access to the current account",
  },
  "integrations.holderAccessDesc": {
    pt: "Integrações com acesso a todas as contas da organização",
    en: "Integrations with access to every account in the organization",
  },
  "integrations.noAccountAccess": {
    pt: "Nenhuma integração tem acesso a esta conta",
    en: "No integration has access to this account",
  },
  "integrations.noHolderAccess": {
    pt: "Nenhuma integração tem acesso a esta organização",
    en: "No integration has access to this organization",
  },
  "integrations.accessReadOnlyNote": {
    pt: "Esta lista é somente de leitura. Funções e revogação são alteradas no Gerenciamento de Acessos.",
    en: "This list is read-only. Roles and revocation are changed in Access Management.",
  },
  "integrations.goToAccess": {
    pt: "Ir para Gerenciamento de Acessos",
    en: "Go to Access Management",
  },
  "integrations.loadAccountFailed": {
    pt: "Não foi possível carregar as integrações da conta.",
    en: "Could not load the account's integrations.",
  },
  "integrations.loadHolderFailed": {
    pt: "Não foi possível carregar as integrações da organização.",
    en: "Could not load the organization's integrations.",
  },
  "integrations.retry": {
    pt: "Tentar de novo",
    en: "Try again",
  },
  "integrations.signInAgain": {
    pt: "Entrar de novo",
    en: "Sign in again",
  },
  "integrations.searchPlaceholder": {
    pt: "Buscar integrações...",
    en: "Search integrations...",
  },
  "integrations.noAccountIntegrations": {
    pt: "Nenhuma integração encontrada para esta conta",
    en: "No integrations found for this account",
  },
  "integrations.noHolderIntegrations": {
    pt: "Nenhuma integração encontrada para esta organização",
    en: "No integrations found for this organization",
  },
  "integrations.active": {
    pt: "Ativo",
    en: "Active",
  },
  "integrations.inactive": {
    pt: "Inativo",
    en: "Inactive",
  },
  "integrations.id": {
    pt: "ID",
    en: "ID",
  },
  "integrations.userId": {
    pt: "ID do Usuário",
    en: "User ID",
  },
  "integrations.created": {
    pt: "Criado em",
    en: "Created",
  },
  "integrations.allowedIPs": {
    pt: "IPs Permitidos",
    en: "Allowed IPs",
  },
  "integrations.deleteConfirmTitle": {
    pt: "Cancelar Integração",
    en: "Cancel Integration",
  },
  "integrations.deleteConfirmDesc": {
    pt: "Tem certeza de que deseja cancelar esta integração? Esta ação não pode ser desfeita.",
    en: "Are you sure you want to cancel this integration? This action cannot be undone.",
  },
  "integrations.deleteConfirmNamePrompt": {
    pt: "Para confirmar, digite o nome da integração:",
    en: "To confirm, please type the integration name:",
  },
  "integrations.deleteConfirmNamePlaceholder": {
    pt: "Digite o nome da integração",
    en: "Enter integration name",
  },
  "integrations.deleteNameMismatch": {
    pt: "Nome não confere",
    en: "Name mismatch",
  },
  "integrations.deleteNameMismatchDesc": {
    pt: "O nome digitado não corresponde ao nome da integração.",
    en: "The entered name does not match the integration name.",
  },
  "integrations.deleteSuccess": {
    pt: "Integração cancelada",
    en: "Integration cancelled",
  },
  "integrations.deleteSuccessDesc": {
    pt: "A integração foi cancelada com sucesso.",
    en: "The integration has been successfully cancelled.",
  },
  "integrations.deleteError": {
    pt: "Erro",
    en: "Error",
  },
  "integrations.cancel": {
    pt: "Cancelar",
    en: "Cancel",
  },
  "integrations.confirm": {
    pt: "Confirmar",
    en: "Confirm",
  },
  "integrations.cancelling": {
    pt: "Cancelando...",
    en: "Cancelling...",
  },
  "integrations.publicKeyTitle": {
    pt: "Chave Pública",
    en: "Public Key",
  },
  "integrations.publicKeyDesc": {
    pt: "Verifique se as informações da chave pública correspondem à que você possui.",
    en: "Verify that the public key information matches the one you have on your side.",
  },
  "integrations.loadingPublicKey": {
    pt: "Carregando chave pública...",
    en: "Loading public key...",
  },
  "integrations.integrationName": {
    pt: "Integração",
    en: "Integration",
  },
  "integrations.publicKeyLabel": {
    pt: "Chave Pública",
    en: "Public Key",
  },
  "integrations.noPublicKey": {
    pt: "Nenhuma chave pública disponível",
    en: "No public key available",
  },
  "integrations.publicKeyError": {
    pt: "Erro",
    en: "Error",
  },
  "integrations.close": {
    pt: "Fechar",
    en: "Close",
  },
  "integrations.newIntegration": {
    pt: "Nova Integração",
    en: "New Integration",
  },
  "integrations.newIntegrationDesc": {
    pt: "Crie uma nova integração com chaves ECDSA",
    en: "Create a new integration with ECDSA keys",
  },
  "integrations.backToIntegrations": {
    pt: "Voltar para Integrações",
    en: "Back to Integrations",
  },
  "integrations.integrationNameLabel": {
    pt: "Nome da Integração",
    en: "Integration Name",
  },
  "integrations.integrationNamePlaceholder": {
    pt: "Ex: Minha Integração de Investimentos",
    en: "Ex: My Investment Integration",
  },
  "integrations.profiles": {
    pt: "Funções",
    en: "Roles",
  },
  "integrations.loadingRoles": {
    pt: "Carregando funções...",
    en: "Loading roles...",
  },
  "integrations.selectProfile": {
    pt: "Selecione a função",
    en: "Select role",
  },
  "integrations.addProfile": {
    pt: "Adicionar Função",
    en: "Add Role",
  },
  "integrations.optional": {
    pt: "opcional",
    en: "optional",
  },
  "integrations.allowedIPsPlaceholder": {
    pt: "Ex: 192.168.1.1, 10.0.0.5\nUm IP por linha ou separado por vírgula",
    en: "Ex: 192.168.1.1, 10.0.0.5\nOne IP per line or separated by comma",
  },
  "integrations.allowedIPsHint": {
    pt: "Deixe em branco para permitir todos os IPs",
    en: "Leave blank to allow all IPs",
  },
  "integrations.keyConfiguration": {
    pt: "Configuração de Chaves",
    en: "Key Configuration",
  },
  "integrations.generateKeys": {
    pt: "Gerar chaves automaticamente",
    en: "Generate keys automatically",
  },
  "integrations.importPublicKey": {
    pt: "Importar minha chave pública",
    en: "Import my public key",
  },
  "integrations.clickToUpload": {
    pt: "Clique para fazer upload",
    en: "Click to upload",
  },
  "integrations.orDragFile": {
    pt: "ou arraste o arquivo",
    en: "or drag file",
  },
  "integrations.fileTypes": {
    pt: "Arquivo .pem, .pub ou .txt",
    en: "File .pem, .pub or .txt",
  },
  "integrations.orPasteKey": {
    pt: "Ou cole sua chave pública",
    en: "Or paste your public key",
  },
  "integrations.pastePublicKeyPlaceholder": {
    pt: "-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----",
    en: "-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----",
  },
  "integrations.aboutECDSAKeys": {
    pt: "Sobre as chaves ECDSA",
    en: "About ECDSA Keys",
  },
  "integrations.aboutECDSAKeysGenerate": {
    pt: "O sistema gerará um par de chaves criptográficas ECDSA (Elliptic Curve Digital Signature Algorithm) e enviará a chave pública para nós:",
    en: "The system will generate a pair of ECDSA (Elliptic Curve Digital Signature Algorithm) cryptographic keys and upload the public key to us:",
  },
  "integrations.aboutECDSAKeysImport": {
    pt: "Você importará sua própria chave pública ECDSA e nós a usaremos para validar a assinatura digital das suas requisições Veltiz:",
    en: "You will import your own ECDSA public key and we will use it to validate the digital signature of your Veltiz requests:",
  },
  "integrations.publicKeyUsageDesc": {
    pt: "Nós a usaremos para validar a assinatura digital das suas requisições Veltiz.",
    en: "We will use it to validate the digital signature of your Veltiz requests.",
  },
  "integrations.privateKey": {
    pt: "Chave Privada",
    en: "Private Key",
  },
  "integrations.privateKeyDesc": {
    pt: "Mantenha em segredo e NUNCA compartilhe com ninguém. Você a usará para assinar todas as requisições Veltiz.",
    en: "Keep it secret and NEVER share it with anyone. You will use it to sign all Veltiz requests.",
  },
  "integrations.generateAndCreate": {
    pt: "Gerar Chaves e Criar Integração",
    en: "Generate Keys and Create Integration",
  },
  "integrations.createIntegration": {
    pt: "Criar Integração",
    en: "Create Integration",
  },
  "integrations.creating": {
    pt: "Criando...",
    en: "Creating...",
  },
  "integrations.nameRequired": {
    pt: "Nome obrigatório",
    en: "Name required",
  },
  "integrations.nameRequiredDesc": {
    pt: "Por favor, insira um nome para a integração",
    en: "Please enter a name for the integration",
  },
  "integrations.roleRequired": {
    pt: "Função obrigatória",
    en: "Role required",
  },
  "integrations.roleRequiredDesc": {
    pt: "Por favor, selecione pelo menos uma função",
    en: "Please select at least one role",
  },
  "integrations.publicKeyRequired": {
    pt: "Chave pública obrigatória",
    en: "Public key required",
  },
  "integrations.publicKeyRequiredDesc": {
    pt: "Por favor, faça upload ou cole sua chave pública",
    en: "Please upload or paste your public key",
  },
  "integrations.createError": {
    pt: "Erro",
    en: "Error",
  },
  "integrations.createUnreadable": {
    pt: "A integração pode ter sido criada, mas não foi possível ler a resposta. Verifique a lista de integrações antes de tentar novamente.",
    en: "The integration may have been created, but the response could not be read. Check the integrations list before you try again.",
  },
  "integrations.createSuccess": {
    pt: "Integração criada",
    en: "Integration created",
  },
  "integrations.createSuccessDesc": {
    pt: "A integração foi criada com sucesso.",
    en: "The integration has been successfully created.",
  },
  "integrations.keysGenerated": {
    pt: "Chaves Geradas!",
    en: "Keys Generated!",
  },
  "integrations.keysGeneratedDesc": {
    pt: "Salve suas chaves em local seguro. A chave privada não será exibida novamente.",
    en: "Save your keys in a secure location. The private key will not be displayed again.",
  },


  "integrations.copied": {
    pt: "Copiado!",
    en: "Copied!",
  },
  "integrations.copiedToClipboard": {
    pt: "copiado para área de transferência",
    en: "copied to clipboard",
  },
  "integrations.privateKeyWarning": {
    pt: "Guarde esta chave em local seguro. Ela não será exibida novamente.",
    en: "Keep this key in a secure location. It will not be displayed again.",
  },
  "integrations.downloadKeys": {
    pt: "Baixar Chaves",
    en: "Download Keys",
  },
  "integrations.finish": {
    pt: "Concluir",
    en: "Finish",
  },
  "integrations.keysDownloaded": {
    pt: "Chaves baixadas",
    en: "Keys downloaded",
  },
  "integrations.keysDownloadedDesc": {
    pt: "Suas chaves foram baixadas",
    en: "Your keys have been downloaded",
  },
  "integrations.integration": {
    pt: "Integração",
    en: "Integration",
  },
  "integrations.allIPs": {
    pt: "Todos",
    en: "All",
  },

  // Investors
  "investors.title": {
    pt: "Investidores",
    en: "Investors",
  },
  "investors.subtitle": {
    pt: "Visualize e gerencie todos os investidores da plataforma",
    en: "View and manage all platform investors",
  },
  "investors.search": {
    pt: "Buscar investidores...",
    en: "Search investors...",
  },
  "investors.status": {
    pt: "Status",
    en: "Status",
  },
  "investors.active": {
    pt: "Ativo",
    en: "Active",
  },
  "investors.inactive": {
    pt: "Inativo",
    en: "Inactive",
  },
  "investors.pending": {
    pt: "Pendente",
    en: "Pending",
  },
  "investors.balance": {
    pt: "Saldo Total",
    en: "Total Balance",
  },
  "investors.transactions": {
    pt: "Transações",
    en: "Transactions",
  },
  "investors.empty": {
    pt: "—",
    en: "—",
  },
  "investors.viewDetails": {
    pt: "Ver detalhes",
    en: "View details",
  },
  "investors.error": {
    pt: "Erro",
    en: "Error",
  },
  "investors.backToConsole": {
    pt: "Voltar ao Início",
    en: "Back to Home",
  },
  "investors.noInvestors": {
    pt: "Nenhum investidor encontrado",
    en: "No investors found",
  },
  "investors.noResults": {
    pt: "Nenhum investidor encontrado com sua busca",
    en: "No investors found matching your search",
  },
  "investors.newInvestor": {
    pt: "Novo Investidor",
    en: "New Investor",
  },
  "investors.newInvestorDesc": {
    pt: "Registre um novo investidor para esta conta",
    en: "Register a new investor for this account",
  },
  "investors.name": {
    pt: "Nome",
    en: "Name",
  },
  "investors.namePlaceholder": {
    pt: "Digite o nome do investidor",
    en: "Enter investor name",
  },
  "investors.nameRequired": {
    pt: "Nome é obrigatório",
    en: "Name is required",
  },
  "investors.taxId": {
    pt: "CPF/CNPJ",
    en: "Tax ID (CPF/CNPJ)",
  },
  "investors.taxIdPlaceholder": {
    pt: "123.456.789-09 ou 12.345.678/0001-90",
    en: "123.456.789-09 or 12.345.678/0001-90",
  },
  "investors.taxIdRequired": {
    pt: "CPF/CNPJ é obrigatório",
    en: "Tax ID is required",
  },
  "investors.createPortfolio": {
    pt: "Criar uma carteira junto com este investidor",
    en: "Create a portfolio at the same time as this investor",
  },
  "investors.portfolioName": {
    pt: "Nome da carteira",
    en: "Portfolio name",
  },
  "investors.portfolioNamePlaceholder": {
    pt: "Digite o nome da carteira",
    en: "Enter portfolio name",
  },
  "investors.portfolioNameRequired": {
    pt: "Nome da carteira é obrigatório",
    en: "Portfolio name is required",
  },
  "investors.taxIdHint": {
    pt: "Digite CPF (pessoa física) ou CNPJ (pessoa jurídica)",
    en: "Enter CPF (individual) or CNPJ (company)",
  },
  "investors.cancel": {
    pt: "Cancelar",
    en: "Cancel",
  },
  "investors.create": {
    pt: "Criar Investidor",
    en: "Create Investor",
  },
  "investors.creating": {
    pt: "Criando...",
    en: "Creating...",
  },
  "investors.investorCreated": {
    pt: "Investidor Criado",
    en: "Investor Created",
  },
  "investors.investorCreatedDesc": {
    pt: "O investidor foi registrado com sucesso.",
    en: "The investor has been successfully registered.",
  },
  "investors.portfolioCreatedDesc": {
    pt: "A carteira \"{name}\" foi criada.",
    en: "The portfolio \"{name}\" was created.",
  },
  "investors.noScopeContext": {
    pt: "Contexto de escopo não disponível",
    en: "No scope context available",
  },

  // Investor Detail
  "investorDetail.title": {
    pt: "Histórico do Investidor",
    en: "Investor History",
  },
  "investorDetail.subtitle": {
    pt: "Todas as transações registradas para este investidor",
    en: "All transactions recorded for this investor",
  },
  "investorDetail.back": {
    pt: "Voltar para investidores",
    en: "Back to investors",
  },
  "investorDetail.portfolioCount": {
    pt: "{n} carteira(s)",
    en: "{n} portfolio(s)",
  },
  "investorDetail.noPortfolio": {
    pt: "Este investidor ainda não tem carteira",
    en: "This investor has no portfolio yet",
  },
  "investorDetail.noPortfolioHint": {
    pt: "Crie uma carteira para começar a registrar transações.",
    en: "Create a portfolio to start recording transactions.",
  },
  "investorDetail.goToPortfolios": {
    pt: "Ir para Carteiras",
    en: "Go to Portfolios",
  },
  "investorDetail.noTransactions": {
    pt: "Nenhuma transação registrada",
    en: "No transactions recorded",
  },
  "investorDetail.noMatches": {
    pt: "Nenhuma transação corresponde aos filtros",
    en: "No transactions match the current filters",
  },
  "investorDetail.noMatchesPartial": {
    pt: "Nenhuma transação carregada corresponde aos filtros; esta lista está incompleta",
    en: "No loaded transaction matches the current filters; this list is incomplete",
  },
  "investorDetail.loadFailed": {
    pt: "Não foi possível carregar as transações",
    en: "Could not load the transactions",
  },
  "investorDetail.portfoliosLoadFailed": {
    pt: "Não foi possível carregar as carteiras deste investidor",
    en: "Could not load this investor's portfolios",
  },
  "investorDetail.partialFailure": {
    pt: "{n} de {total} carteiras não puderam ser carregadas; esta lista está incompleta.",
    en: "{n} of {total} portfolios could not be loaded; this list is incomplete.",
  },
  "investorDetail.truncated": {
    pt: "Mostrando apenas os primeiros resultados.",
    en: "Showing the first results only.",
  },
  "investorDetail.grossUnavailable": {
    pt: "Valor não representável",
    en: "Amount not representable",
  },
  "investorDetail.noTransactionsPartial": {
    pt: "Nenhuma transação nas carteiras consultadas; esta lista está incompleta",
    en: "No transactions in the portfolios that were read; this list is incomplete",
  },
  "investorDetail.portfoliosOmitted": {
    pt: "{n} carteira(s) não foram consultadas; as transações delas estão totalmente ausentes. Mostrando {queried}.",
    en: "{n} portfolio(s) were not queried; their transactions are missing entirely. Showing {queried}.",
  },
  "investorDetail.morePortfoliosExist": {
    pt: "Este investidor tem mais carteiras do que as consultadas; as transações delas estão ausentes",
    en: "This investor has more portfolios than were queried; their transactions are missing",
  },
  "investorDetail.identityUnavailable": {
    pt: "Não foi possível confirmar este investidor",
    en: "This investor could not be confirmed",
  },
  "investorDetail.notFound": {
    pt: "Investidor não encontrado",
    en: "Investor not found",
  },
  "investorDetail.retry": {
    pt: "Tentar novamente",
    en: "Retry",
  },
  "investorDetail.filters": {
    pt: "Filtrar",
    en: "Filter",
  },
  "investorDetail.filterTitle": {
    pt: "Filtrar Transações",
    en: "Filter Transactions",
  },
  "investorDetail.filterType": {
    pt: "Tipo de Transação",
    en: "Transaction Type",
  },
  "investorDetail.filterStatus": {
    pt: "Status",
    en: "Status",
  },
  "investorDetail.filterPeriod": {
    pt: "Período",
    en: "Period",
  },
  "investorDetail.startDate": {
    pt: "Data inicial",
    en: "Start date",
  },
  "investorDetail.endDate": {
    pt: "Data final",
    en: "End date",
  },
  "investorDetail.applyFilters": {
    pt: "Aplicar Filtros",
    en: "Apply Filters",
  },
  "investorDetail.clearFilters": {
    pt: "Limpar Filtros",
    en: "Clear Filters",
  },
  "investorDetail.logsTitle": {
    pt: "Logs da Transação",
    en: "Transaction Logs",
  },
  "investorDetail.logsEmpty": {
    pt: "Nenhum log para esta transação",
    en: "No logs for this transaction",
  },
  "investorDetail.logsFailed": {
    pt: "Não foi possível carregar os logs",
    en: "Could not load the logs",
  },
  "investorDetail.noScope": {
    pt: "Sessão ou conta indisponível. Entre novamente para ver este investidor.",
    en: "No session or account available. Sign in again to view this investor.",
  },
  "investorDetail.signInAgain": {
    pt: "Entrar novamente",
    en: "Sign in again",
  },
  "investorDetail.colPortfolio": {
    pt: "Carteira",
    en: "Portfolio",
  },

  // External trade events
  "tradeEvent.created": {
    pt: "Criado",
    en: "Created",
  },
  "tradeEvent.booked": {
    pt: "Registrado no ledger",
    en: "Booked in the ledger",
  },
  "tradeEvent.success": {
    pt: "Concluído",
    en: "Completed",
  },
  "tradeEvent.canceled": {
    pt: "Cancelado",
    en: "Canceled",
  },
  "tradeEvent.voided": {
    pt: "Anulado",
    en: "Voided",
  },
  "tradeEvent.failed": {
    pt: "Falhou",
    en: "Failed",
  },
  "tradeReason.instrument-ledger-unavailable": {
    pt: "O ledger do instrumento não pôde ser resolvido.",
    en: "The instrument's ledger could not be resolved.",
  },
  "tradeReason.symbol-scale-finer-than-ledger": {
    pt: "A quantidade negociada é mais precisa do que o ledger do instrumento permite.",
    en: "The traded quantity is more precise than the instrument's ledger allows.",
  },
  "tradeReason.statement-creation-failed": {
    pt: "Os extratos de custódia externa não puderam ser criados.",
    en: "The external custody statements could not be created.",
  },
  "tradeReason.transfer-failed": {
    pt: "A negociação não pôde ser registrada no ledger.",
    en: "The trade could not be booked in the ledger.",
  },

  // Pricing
  "pricing.title": { pt: "Precificação", en: "Pricing" },
  "pricing.subtitle": { pt: "Gerencie os preços dos seus instrumentos financeiros", en: "Manage prices for your financial instruments" },
  "pricing.currentPrices": { pt: "Preços Atuais", en: "Current Prices" },
  "pricing.priceHistory": { pt: "Histórico de Preços", en: "Price History" },
  "pricing.instrumentSymbol": { pt: "Instrumento", en: "Instrument" },
  "pricing.buyPrice": { pt: "Preço Compra", en: "Buy Price" },
  "pricing.sellPrice": { pt: "Preço Venda", en: "Sell Price" },
  "pricing.buyRate": { pt: "Taxa Compra", en: "Buy Rate" },
  "pricing.sellRate": { pt: "Taxa Venda", en: "Sell Rate" },
  "pricing.buyStatus": { pt: "Status Compra", en: "Buy Status" },
  "pricing.sellStatus": { pt: "Status Venda", en: "Sell Status" },
  "pricing.expiration": { pt: "Expiração", en: "Expiration" },
  "pricing.expirationTime": { pt: "Hora", en: "Time" },
  "pricing.expirationBuy": { pt: "Expiração Compra", en: "Expiration Buy" },
  "pricing.expirationSell": { pt: "Expiração Venda", en: "Expiration Sell" },
  "pricing.requestedBy": { pt: "Solicitado por", en: "Requested by" },
  "pricing.export": { pt: "Exportar", en: "Export" },
  "pricing.filters": { pt: "Filtros", en: "Filters" },
  "pricing.updated": { pt: "Atualizado", en: "Updated" },
  "pricing.noPricing": { pt: "Sem Precificação", en: "No Pricing" },
  "pricing.updatePricing": { pt: "Atualizar Preço", en: "Update Pricing" },
  "pricing.confirmChange": { pt: "Confirmar Alteração", en: "Confirm Change" },
  "pricing.quantity": { pt: "Quantidade", en: "Quantity" },
  "pricing.pricePositive": { pt: "O preço deve ser maior que zero", en: "Price must be greater than zero" },
  "pricing.quoteType": { pt: "Tipo de Cotação", en: "Quote Type" },
  "pricing.price": { pt: "Preço", en: "Price" },
  "pricing.yield": { pt: "Rendimento", en: "Yield" },
  "pricing.noInstruments": { pt: "Nenhum instrumento cadastrado", en: "No instruments registered" },
  "pricing.searchPlaceholder": { pt: "Buscar instrumento...", en: "Search instrument..." },
  "pricing.modalDescription": {
    pt: "Escolha Compra ou Venda para ver o preço atual e enviar um novo.",
    en: "Choose Buy or Sell to view current pricing and submit new pricing.",
  },
  "pricing.sideBuy": { pt: "Compra", en: "Buy" },
  "pricing.sideSell": { pt: "Venda", en: "Sell" },
  "pricing.statusActive": { pt: "Ativo", en: "Active" },
  "pricing.statusExpired": { pt: "Expirado", en: "Expired" },
  "pricing.statusCanceled": { pt: "Cancelado", en: "Canceled" },
  "pricing.awaitingInput": { pt: "Aguardando entrada de preço", en: "Awaiting pricing input" },
  "pricing.noSidePricing": {
    pt: "Sem preço de {side} definido — envie um novo abaixo.",
    en: "No {side} pricing set — submit a new price below.",
  },
  "pricing.sidePricingNeedsRefresh": {
    pt: "O preço de {side} está {status} — atualize abaixo.",
    en: "The {side} pricing has {status} — please refresh it below.",
  },
  "pricing.currentSidePricing": {
    pt: "Preço atual de {side}",
    en: "Current {side} pricing",
  },
  "pricing.yieldDerived": { pt: "Rendimento (derivado)", en: "Yield (derived)" },
  "pricing.yieldAnbima": { pt: "Rendimento (ANBIMA)", en: "Yield (ANBIMA)" },
  "pricing.previewComputing": { pt: "calculando…", en: "computing…" },
  "pricing.previewUnavailable": {
    pt: "prévia indisponível, usando estimativa local",
    en: "preview unavailable, using local estimate",
  },
  "pricing.previewAnbima": { pt: "ANBIMA", en: "ANBIMA" },
  "pricing.previewLocalEstimate": { pt: "estimativa local", en: "local estimate" },
  "pricing.expires": { pt: "Expira", en: "Expires" },
  "pricing.autoCalculated": { pt: "Calculado automaticamente", en: "Auto-calculated" },
  "pricing.priceYieldLinked": {
    pt: "Preço e Rendimento estão vinculados — um é derivado automaticamente do outro.",
    en: "Price and Yield are linked — one is derived automatically from the other.",
  },
  "pricing.close": { pt: "Fechar", en: "Close" },
  "pricing.cancel": { pt: "Cancelar", en: "Cancel" },
  "pricing.confirm": { pt: "Confirmar", en: "Confirm" },
  "pricing.saving": { pt: "Salvando...", en: "Saving..." },
  "pricing.confirmDescription": {
    pt: "Você está definindo um novo preço de {side} para {symbol}. Isso será publicado no livro de ordens.",
    en: "You are setting a new {side} price for {symbol}. This will be published to the order book.",
  },
  "pricing.side": { pt: "Operação", en: "Side" },
  "pricing.expirationDateRequired": {
    pt: "A data de expiração é obrigatória",
    en: "The expiration date is required",
  },
  "pricing.invalidExpirationTime": {
    pt: "Hora de expiração inválida",
    en: "Invalid expiration time",
  },

  // Orders
  "orders.title": { pt: "Ordens", en: "Orders" },
  "orders.subtitle": { pt: "Gerencie as ordens recebidas dos investidores", en: "Manage orders received from investors" },
  "orders.currentOrders": { pt: "Ordens Atuais", en: "Current Orders" },
  "orders.orderHistory": { pt: "Histórico de Ordens", en: "Order History" },
  "orders.orderId": { pt: "ID da Ordem", en: "Order ID" },
  "orders.received": { pt: "Recebida em", en: "Received" },
  "orders.instrument": { pt: "Instrumento", en: "Instrument" },
  "orders.side": { pt: "Operação", en: "Side" },
  "orders.qty": { pt: "Qtd", en: "Qty" },
  "orders.price": { pt: "Preço", en: "Price" },
  "orders.status": { pt: "Status", en: "Status" },
  "orders.confirm": { pt: "Confirmar", en: "Confirm" },
  "orders.reject": { pt: "Rejeitar", en: "Reject" },
  "orders.confirming": { pt: "Confirmar Execução", en: "Confirm Execution" },
  "orders.rejecting": { pt: "Rejeitar Ordem", en: "Reject Order" },
  "orders.executedQty": { pt: "Quantidade Executada", en: "Executed Quantity" },
  "orders.executedPrice": { pt: "Preço Executado", en: "Executed Price" },
  "orders.noOrders": { pt: "Nenhuma ordem encontrada", en: "No orders found" },
  "orders.pending": { pt: "Pendente", en: "Pending" },
  "orders.confirmed": { pt: "Confirmada", en: "Confirmed" },
  "orders.rejected": { pt: "Rejeitada", en: "Rejected" },
  "orders.canceled": { pt: "Cancelada", en: "Canceled" },
  "orders.expired": { pt: "Expirada", en: "Expired" },
  "orders.event": { pt: "Evento", en: "Event" },
  "orders.requestedBy": { pt: "Solicitado por", en: "Requested by" },
  "orders.export": { pt: "Exportar", en: "Export" },
  "orders.refresh": { pt: "Atualizar", en: "Refresh" },
  "orders.filters": { pt: "Filtros", en: "Filters" },
  "orders.orderDetails": { pt: "Detalhes da Ordem", en: "Order Details" },
  "orders.instrumentType": { pt: "Tipo de Instrumento", en: "Instrument Type" },
  "orders.bidMode": { pt: "Modo de Lance", en: "Bid Mode" },
  "orders.quantity": { pt: "Quantidade", en: "Quantity" },
  "orders.quotedPrice": { pt: "Preço Cotado", en: "Quoted Price" },
  "orders.source": { pt: "Origem", en: "Source" },
  "orders.tags": { pt: "Etiquetas", en: "Tags" },
  "orders.execution": { pt: "Execução", en: "Execution" },
  "orders.confirmOrder": { pt: "Confirmar Ordem", en: "Confirm Order" },
  "orders.rejectOrder": { pt: "Rejeitar Ordem", en: "Reject Order" },
  "orders.rejectIrreversibleDesc": {
    pt: "Rejeitar esta ordem é irreversível e o investidor será notificado.",
    en: "Rejecting this order is irreversible and will notify the investor.",
  },
  "orders.confirmExecutionTitle": { pt: "Confirmar execução?", en: "Confirm execution?" },
  "orders.confirmExecutionDesc": {
    pt: "Você está marcando esta ordem como confirmada com:",
    en: "You are marking this order as confirmed with:",
  },
  "orders.rejectOrderTitle": { pt: "Rejeitar esta ordem?", en: "Reject this order?" },
  "orders.rejectOrderDesc": {
    pt: "Isso irá definir permanentemente a ordem {id} como rejeitada. Esta ação não pode ser desfeita.",
    en: "This will permanently set order {id} to rejected. This cannot be undone.",
  },
  "orders.cancel": { pt: "Cancelar", en: "Cancel" },
  "orders.amount": { pt: "Valor", en: "Amount" },
  "orders.searchPlaceholder": {
    pt: "Buscar por instrumento ou ID da ordem…",
    en: "Search by instrument or order ID…",
  },
  "orders.clear": { pt: "Limpar", en: "Clear" },
  "orders.selectedCount": {
    pt: "{n} selecionada(s)",
    en: "{n} selected",
  },
  "orders.pendingCount": {
    pt: "{n} pendente(s)",
    en: "{n} pending",
  },
  "orders.rejectSelected": { pt: "Rejeitar selecionadas", en: "Reject Selected" },
  "orders.clearSelection": { pt: "Limpar seleção", en: "Clear selection" },
  "orders.rejectMultipleTitle": {
    pt: "Rejeitar {n} ordem(ns)?",
    en: "Reject {n} order(s)?",
  },
  "orders.rejectMultipleDesc": {
    pt: "Isso irá definir as ordens pendentes selecionadas como rejeitadas. Esta ação não pode ser desfeita.",
    en: "This will set the selected pending orders to rejected. This action cannot be undone.",
  },

  // User Menu
  "menu.user": {
    pt: "Usuário",
    en: "User",
  },
  "menu.logout": {
    pt: "Sair",
    en: "Logout",
  },
  "menu.settings": {
    pt: "Configurações",
    en: "Settings",
  },
  "menu.switchTheme": {
    pt: "Trocar para",
    en: "Switch to",
  },
  "menu.light": {
    pt: "claro",
    en: "light",
  },
  "menu.dark": {
    pt: "escuro",
    en: "dark",
  },
  "dateField.invalid": {
    pt: "Data inválida",
    en: "Invalid date",
  },
  "dateField.openCalendar": {
    pt: "Abrir calendário",
    en: "Open calendar",
  },
  "dateField.formatHint": {
    pt: "Formato: {format}",
    en: "Format: {format}",
  },
  "settings.title": {
    pt: "Configurações",
    en: "Settings",
  },
  "settings.description": {
    pt: "Suas configurações são suas e valem em todas as organizações e contas que você acessa.",
    en: "Your settings are your own and apply in every organization and account you can reach.",
  },
  "settings.preferences": {
    pt: "Preferências",
    en: "Preferences",
  },
  "settings.language": {
    pt: "Idioma",
    en: "Language",
  },
  "settings.dateFormat": {
    pt: "Formato de data",
    en: "Date format",
  },
  "settings.currency": {
    pt: "Moeda",
    en: "Currency",
  },
  "settings.currencyHint": {
    pt: "A moeda em que o NAV de um portfólio abre. O seletor do portfólio não altera esta configuração.",
    en: "The currency a portfolio's NAV opens in. The portfolio's own toggle does not change this setting.",
  },
  "settings.numberFormat": {
    pt: "Formato de números",
    en: "Number format",
  },
  "settings.numberFormatHint": {
    pt: "Como valores, quantidades e preços são exibidos, e como digitá-los.",
    en: "How amounts, quantities and prices are shown, and how to type them.",
  },
  "settings.save": {
    pt: "Salvar",
    en: "Save",
  },
  "settings.saving": {
    pt: "Salvando...",
    en: "Saving...",
  },
  "settings.saved": {
    pt: "Configurações salvas",
    en: "Settings saved",
  },
  "settings.saveUnreadable": {
    pt: "Não foi possível ler a resposta do servidor, então a alteração pode não ter sido salva. A página mostra as configurações armazenadas.",
    en: "The server's answer could not be read, so the change may not be saved. The page shows the stored settings.",
  },
  "settings.loading": {
    pt: "Carregando suas configurações...",
    en: "Loading your settings...",
  },
  "settings.loadFailed": {
    pt: "Não foi possível carregar suas configurações.",
    en: "Your settings could not be loaded.",
  },
  "settings.retry": {
    pt: "Tentar novamente",
    en: "Retry",
  },
  "settings.notifications": {
    pt: "Notificações",
    en: "Notifications",
  },
  "settings.notifyOnNewOrders": {
    pt: "Notificar sobre novas ordens",
    en: "Notify on new orders",
  },
  "settings.notificationsScope": {
    pt: "Vale em todas as organizações e contas que você acessa. Você só é notificado sobre ordens das contas às quais tem acesso.",
    en: "Applies in every organization and account you can reach. You are notified only about orders in accounts you have access to.",
  },
  "settings.notificationsLoadFailed": {
    pt: "Não foi possível carregar sua escolha de notificações, então ela não pode ser alterada agora.",
    en: "Your notification choice could not be loaded, so it cannot be changed now.",
  },
  "settings.notificationsSaveFailed": {
    pt: "Não foi possível salvar sua escolha de notificações. A caixa mostra o que está armazenado.",
    en: "Your notification choice could not be saved. The box shows what is stored.",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(storedLanguage);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

